'use client';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

type Asset = {
  id: string; name: string; group: string; symbol: string; quote: string; scale: number; digits: number; payoutBps: number; maxStakeCents: number;
  liveMaxAgeMs?: number; listed?: boolean; change24h?: number; range24h?: number; perMin?: number; volumeM?: number; icon?: string;
};

// The coin's original logo (served from our domain); its letter if there is none or it fails to load.
function AssetIcon({ a, small = false }: { a?: Asset; small?: boolean }) {
  const [broken, setBroken] = useState(false);
  useEffect(() => setBroken(false), [a?.icon]);
  const size = small ? 18 : 34;
  return a?.icon && !broken ? (
    <span className={`asset-icon logo ${small ? 'sm' : ''}`}>
      <img src={a.icon} alt="" width={size} height={size} loading="lazy" onError={() => setBroken(true)} />
    </span>
  ) : (
    <span className={`asset-icon ${small ? 'sm' : ''}`}>{a?.symbol ?? '…'}</span>
  );
}
type Candle = { time: number; open: number; high: number; low: number; close: number };
// One message of the live feed: the newest exchange trade plus the high/low of the burst it ends.
type Tick = { id: string; price: number; atMs: number; hi: number; lo: number; n: number };
type Quote = { source: string; assetId: string; tradeId: string; price: number; atMs: number; receivedAtMs: number };
type Trade = {
  id: string; assetId: string; direction: 'up' | 'down'; stakeCents: number; profitCents: number; payoutBps: number;
  acceptedAtMs: number; expiresAtMs: number; durationMs: number | null; entryWindowMs?: number; expiryMode: 'timer' | 'time';
  openPrice: number | null; closePrice: number | null; payoutCents: number | null; status: string;
  openQuote: Quote | null; closeQuote: Quote | null; refundReason: string | null; settledAtMs: number | null;
};
type Policy = { durations: number[]; minStakeCents: number; maxActive: number; entryWindowMs: number; cutoffMs: number; demoBalanceCents: number };
type Snapshot = {
  wallet?: { address: string; linkedAtMs: number } | null;
  coreVersion: string; serverNowMs: number; asOfMs: number; policy: Policy; assets: Asset[]; assetsVersion: string;
  user: { id: number; firstName: string | null; username: string | null };
  botUsername: string | null;
  balanceCents: number; lockedCents: number; absoluteSlots: number[];
  referral: Referral;
  duels?: Duel[];
  marketAssetId: string; marketPeriodSeconds: number;
  market: { source: string; status: string; message: string; quoteAtMs: number | null; chartMessage: string | null; entryWindowMs?: number; cutoffMs?: number; blockedDurations?: number[] };
  quote: Quote | null; candles: Candle[]; trades: Trade[];
  stats: { total: number; open: number; pendingEntry: number; awaitingPrice: number; closed: number; wins: number; losses: number; ties: number; voids: number; netCents: number };
};
type Action = { path: string; body: { id: string; [key: string]: unknown } };
type Reply = { trade?: Trade; replayed?: boolean; balanceCents?: number; error?: { code: string; message: string } };
type Toast = { id: string; tone: 'win' | 'loss' | 'flat' | 'info'; title: string; body: string; asset?: Asset; tradeId?: string; leaving?: boolean; pose?: Pose };
type Referral = { invitedBy: string | null; bonusPaid: boolean; invited: number; played: number; bonusCents: number };
type Ranked = { rank: number | null; name?: string; netCents: number; trades: number; wins: number; me?: boolean; ton?: boolean };
type Board = { fromMs: number; toMs: number; minTrades: number; players: number; top: Ranked[]; me: Ranked; referral: Referral; inviteLink: string | null };
type Duel = {
  id: string; assetId: string; stakeCents: number; feeBps: number; durationMs: number; visibility: 'lobby' | 'link';
  status: 'open' | 'pending' | 'live' | 'won' | 'tie' | 'void' | 'cancelled' | 'expired';
  creator: { name: string; side: 'up' | 'down' }; opponent: { name: string; side: 'up' | 'down' } | null; you: 'creator' | 'opponent' | null;
  createdAtMs: number; acceptUntilMs: number; acceptedAtMs: number | null; endsAtMs: number | null;
  openQuote: Quote | null; openPrice: number | null; closeQuote: Quote | null; closePrice: number | null;
  winner: 'creator' | 'opponent' | null; feeCents: number | null; refundReason: string | null; settledAtMs: number | null;
};
type DuelBoard = { arenas: string[]; lobby: Duel[]; mine: Duel[]; prices: Record<string, number | null>; rules: { maxStakeCents: number; feeBps: number; acceptMs: number }; serverNowMs: number };
type Replay = { tradeId: string; entry: Quote; close: Quote; expiresAtMs: number; points: { atMs: number; price: number }[] };
type TelegramWebApp = {
  initData: string; ready: () => void; expand: () => void; disableVerticalSwipes?: () => void;
  openLink?: (url: string) => void; openTelegramLink?: (url: string) => void; initDataUnsafe?: { start_param?: string };
  HapticFeedback?: { impactOccurred: (s: string) => void; notificationOccurred: (s: string) => void };
  shareMessage?: (preparedId: string, done?: (sent: boolean) => void) => void; isVersionAtLeast?: (v: string) => boolean;
};

const PENDING = 'direction-lab-pending-v4';

// Older Telegram webviews (iOS < 16, old Android WebView) have no canvas roundRect.
if (typeof CanvasRenderingContext2D !== 'undefined' && !('roundRect' in CanvasRenderingContext2D.prototype)) {
  (CanvasRenderingContext2D.prototype as unknown as { roundRect: (x: number, y: number, w: number, h: number, r: number) => void }).roundRect = function (
    this: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number,
  ) {
    const rr = Math.min(r, w / 2, h / 2);
    this.moveTo(x + rr, y);
    this.arcTo(x + w, y, x + w, y + h, rr);
    this.arcTo(x + w, y + h, x, y + h, rr);
    this.arcTo(x, y + h, x, y, rr);
    this.arcTo(x, y, x + w, y, rr);
    this.closePath();
  };
}
const PREFS = 'direction-lab-prefs-v3';
const STAKES = [1, 2, 5, 10, 25, 50, 100, 200, 500, 1000];

const money = (n: number) => new Intl.NumberFormat('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n / 100);
// whole demo units for round amounts like bonuses: 100000 → '1 000'
const whole = (n: number) => new Intl.NumberFormat('ru-RU').format(Math.round(n / 100));
// exact amount: whole units when round, otherwise with kopecks (9,50 Д)
const amt = (n: number) => (n % 100 ? money(n) : whole(n));
const signed = (n: number) => (n > 0 ? '+' : n < 0 ? '−' : '') + money(Math.abs(n));
const clock = (n: number, seconds = true) =>
  new Intl.DateTimeFormat('ru-RU', { timeZone: 'Europe/Moscow', hour: '2-digit', minute: '2-digit', ...(seconds ? { second: '2-digit' } : {}), hour12: false }).format(n);
const fmt = (n: number, a: Asset) => (n / a.scale).toFixed(a.digits);
const MONO = 'ui-monospace, "SF Mono", Menlo, Consolas, monospace';
// Any CSS colour → rgba() with the given alpha (canvas gradients and glows need explicit alpha).
const withAlpha = (ctx: CanvasRenderingContext2D, color: string, alpha: number) => {
  ctx.fillStyle = color;
  const c = String(ctx.fillStyle); // the canvas normalises it to '#rrggbb' or 'rgba(r, g, b, a)'
  if (c.startsWith('#')) {
    const n = parseInt(c.slice(1), 16);
    return `rgba(${n >> 16}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
  }
  const m = c.match(/[\d.]+/g);
  return m ? `rgba(${m[0]}, ${m[1]}, ${m[2]}, ${alpha})` : color;
};
const countdown = (ms: number) => {
  const s = Math.max(0, Math.ceil(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};
const durationLabel = (sec: number) => (sec < 60 ? `${sec} с` : `${sec / 60} мин`);
const tg = () => (typeof window === 'undefined' ? undefined : (window as unknown as { Telegram?: { WebApp?: TelegramWebApp } }).Telegram?.WebApp);
// Vibration is off until the trader turns it on in the settings. Even then at most one buzz every
// 0.4 s: ten trades closing together must not make the phone rattle.
let vibroOn = false,
  lastBuzz = 0;
const haptic = (kind: 'impact' | 'success' | 'error' | 'warning' | 'light' | 'soft' | 'rigid') => {
  if (!vibroOn || performance.now() - lastBuzz < 400) return;
  lastBuzz = performance.now();
  try {
    const h = tg()?.HapticFeedback;
    if (kind === 'impact') h?.impactOccurred('medium');
    else if (kind === 'light' || kind === 'soft' || kind === 'rigid') h?.impactOccurred(kind);
    else h?.notificationOccurred(kind);
  } catch {}
};
// 1.234 → "1,2%"; with a sign for changes; tiny changes show as a plain "0,0%".
const pct = (n: number, signed = false) => {
  const r = Math.round(n * 10) / 10;
  return `${signed && r > 0 ? '+' : ''}${(r === 0 ? 0 : r).toFixed(1).replace('.', ',').replace('-', '−')}%`;
};
const clockMs = (n: number) => `${clock(n)}.${String(n % 1000).padStart(3, '0')}`;
// Public Binance data for one aggregate trade: anyone can check the price and time we used.
const verifyUrl = (q: Quote) => `https://data-api.binance.vision/api/v3/aggTrades?symbol=${q.assetId}&fromId=${q.tradeId}&limit=1`;
const openExternal = (url: string) => {
  const w = tg();
  if (w?.openLink) w.openLink(url);
  else window.open(url, '_blank', 'noopener');
};
// A number that glides to its new value (balance after a trade) instead of jumping.
function useTween(value: number, ms = 450) {
  const [shown, setShown] = useState(value);
  const from = useRef(value);
  useEffect(() => {
    const start = performance.now(),
      a = from.current;
    if (a === value) return;
    let frame = 0;
    const step = () => {
      const k = Math.min(1, (performance.now() - start) / ms),
        v = Math.round(a + (value - a) * (1 - (1 - k) ** 3));
      from.current = v;
      setShown(v);
      if (k < 1) frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [value, ms]);
  return shown;
}

// Share of trades a player must win just to break even at this payout: 1 / (1 + payout).
const breakEven = (payoutBps: number) => ((10_000 / (10_000 + payoutBps)) * 100).toFixed(1).replace('.', ',');
// Every API call carries the signed Telegram init data; outside Telegram the server may allow a dev user.
const auth = (): Record<string, string> => ({ Authorization: `tma ${tg()?.initData || 'dev'}` });
const readPrefs = () => {
  try {
    return JSON.parse(localStorage.getItem(PREFS) ?? '{}') as Record<string, unknown>;
  } catch {
    return {};
  }
};

function tradeState(t: Trade, now: number, live: number | null) {
  if (t.status === 'pending') {
    // the entry is the first exchange trade after the order; on quieter pairs that can take a while
    const left = t.acceptedAtMs + (t.entryWindowMs ?? 10_000) - now;
    return { label: left > 1500 ? `Ждём первую сделку на бирже · ${Math.ceil(left / 1000)} с` : 'Фиксируем цену входа', tone: 'flat' as const };
  }
  if (t.status === 'open') {
    if (t.expiresAtMs <= now) return { label: 'Ждём цену закрытия', tone: 'flat' as const };
    const winning = live === null || t.openPrice === null ? null : live === t.openPrice ? null : (live > t.openPrice) === (t.direction === 'up');
    return { label: countdown(t.expiresAtMs - now), tone: winning === null ? ('flat' as const) : winning ? ('win' as const) : ('loss' as const) };
  }
  if (t.status === 'win') return { label: `+${money(t.profitCents)} Д`, tone: 'win' as const };
  if (t.status === 'loss') return { label: `−${money(t.stakeCents)} Д`, tone: 'loss' as const };
  if (t.status === 'tie') return { label: 'Цена не изменилась, сумма возвращена', tone: 'flat' as const };
  return { label: t.refundReason === 'no_entry' ? 'Не было сделок на рынке, сумма возвращена' : 'Источник не ответил, сумма возвращена', tone: 'flat' as const };
}

function Chart({ s, asset, now, previewExpiry, kind, flashAt }: { s: Snapshot; asset: Asset; now: number; previewExpiry: number; kind: 'candles' | 'line'; flashAt: number }) {
  const ref = useRef<HTMLCanvasElement>(null),
    dot = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas || !s.quote) return;
    const draw = () => {
      const { width: w, height: h } = canvas.getBoundingClientRect(),
        dpr = window.devicePixelRatio || 1;
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      const ctx = canvas.getContext('2d');
      if (!ctx || !s.quote) return;
      ctx.scale(dpr, dpr);
      const css = getComputedStyle(canvas),
        v = (n: string) => css.getPropertyValue(n).trim();
      const C = { text: v('--text'), hint: v('--hint'), grid: v('--grid'), up: v('--up'), down: v('--down'), accent: v('--accent'), bg: v('--bg') };
      const periodMs = s.marketPeriodSeconds * 1000,
        quote = s.quote,
        trades = s.trades.filter((t) => t.assetId === asset.id && t.status === 'open' && t.openQuote);
      ctx.font = `11px ${MONO}`;
      // the price scale is as wide as the longest price label (cheap coins have many digits)
      const axis = Math.max(66, Math.ceil(ctx.measureText(fmt(quote.price, asset)).width) + 18),
        top = 14,
        bottom = 22,
        plotW = w - axis,
        plotH = h - top - bottom;
      // With an open trade the chart zooms in on it: a couple of trade lengths of history before
      // the entry, so even a 30-second trade is wide enough to see its capsule.
      let tMin = s.candles.length ? s.candles[0].time : now - 60 * periodMs;
      if (trades.length) {
        const entry = Math.min(...trades.map((t) => t.openQuote!.atMs)),
          length = Math.max(...trades.map((t) => t.expiresAtMs - t.openQuote!.atMs));
        tMin = Math.max(tMin, Math.floor((entry - Math.max(2.5 * length, 6 * periodMs)) / periodMs) * periodMs);
      }
      const bars = s.candles.filter((b) => b.time >= tMin),
        span = Math.max(now - tMin, periodMs * (trades.length ? 6 : 10));
      const ends = [previewExpiry, ...trades.map((t) => t.expiresAtMs)];
      const tMax = Math.min(now + span, Math.max(now + span * 0.3, ...ends.map((e) => e + periodMs * 2)));
      const x = (t: number) => ((t - tMin) / (tMax - tMin)) * plotW;
      const prices = [...bars.flatMap((b) => [b.low, b.high]), quote.price, ...trades.map((t) => t.openPrice as number)];
      let lo = Math.min(...prices),
        hi = Math.max(...prices);
      const pad = Math.max((hi - lo) * 0.2, 3);
      lo -= pad;
      hi += pad;
      const y = (p: number) => top + ((hi - p) / (hi - lo)) * plotH;
      ctx.font = `11px ${MONO}`;
      ctx.textBaseline = 'middle';
      // grid + price axis on round values
      const raw = (hi - lo) / 4,
        mag = 10 ** Math.floor(Math.log10(Math.max(raw, 1))),
        tick = [1, 2, 5, 10].map((k) => k * mag).find((k) => k >= raw) ?? 10 * mag;
      for (let p = Math.ceil(lo / tick) * tick; p <= hi; p += tick) {
        ctx.strokeStyle = C.grid;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(0, Math.round(y(p)) + 0.5);
        ctx.lineTo(plotW, Math.round(y(p)) + 0.5);
        ctx.stroke();
        // instrument-style tick on the price scale
        ctx.strokeStyle = C.hint;
        ctx.beginPath();
        ctx.moveTo(plotW, Math.round(y(p)) + 0.5);
        ctx.lineTo(plotW + 4, Math.round(y(p)) + 0.5);
        ctx.stroke();
        ctx.fillStyle = C.hint;
        if (Math.abs(y(p) - y(quote.price)) > 14) ctx.fillText(fmt(Math.round(p), asset), plotW + 8, y(p));
      }
      // time labels
      ctx.textAlign = 'center';
      const step = Math.max(periodMs, Math.ceil(span / 4 / 60_000) * 60_000);
      for (let t = Math.ceil(tMin / step) * step; t < tMax; t += step) {
        if (kind === 'line') {
          // vertical divisions, like an oscilloscope screen
          ctx.strokeStyle = C.grid;
          ctx.setLineDash([2, 4]);
          ctx.beginPath();
          ctx.moveTo(Math.round(x(t)) + 0.5, top);
          ctx.lineTo(Math.round(x(t)) + 0.5, top + plotH);
          ctx.stroke();
          ctx.setLineDash([]);
        }
        ctx.fillStyle = C.hint;
        ctx.fillText(clock(t, step < 60_000), x(t), h - 10);
      }
      ctx.textAlign = 'left';
      if (kind === 'line') {
        // line through each period's close (at the period's end), ending at the live price
        const pts = bars.map((b) => [x(Math.min(b.time + periodMs, now)), y(b.close)]);
        pts.push([x(now), y(quote.price)]);
        // soft glow under the trace instead of a flat fill
        const under = ctx.createLinearGradient(0, top, 0, top + plotH);
        under.addColorStop(0, withAlpha(ctx, C.accent, 0.2));
        under.addColorStop(1, withAlpha(ctx, C.accent, 0));
        ctx.beginPath();
        pts.forEach(([px, py], i) => (i ? ctx.lineTo(px, py) : ctx.moveTo(px, py)));
        ctx.lineTo(pts[pts.length - 1][0], top + plotH);
        ctx.lineTo(pts[0][0], top + plotH);
        ctx.closePath();
        ctx.fillStyle = under;
        ctx.fill();
        // oscilloscope trace: the older part fades out (afterglow), the fresh end glows
        ctx.strokeStyle = C.accent;
        ctx.shadowColor = C.accent;
        ctx.lineWidth = 1.8;
        ctx.lineJoin = 'round';
        ctx.lineCap = 'round';
        for (let i = 1; i < pts.length; i++) {
          const k = i / (pts.length - 1);
          ctx.globalAlpha = 0.15 + 0.85 * k * k;
          ctx.shadowBlur = k > 0.6 ? 12 * k : 0;
          ctx.beginPath();
          ctx.moveTo(pts[i - 1][0], pts[i - 1][1]);
          ctx.lineTo(pts[i][0], pts[i][1]);
          ctx.stroke();
        }
        ctx.globalAlpha = 1;
        ctx.shadowBlur = 0;
        ctx.lineWidth = 1;
        ctx.lineCap = 'butt';
      } else {
        // candles
        const bw = Math.max(1.5, (x(tMin + periodMs) - x(tMin)) * 0.62);
        for (const b of bars) {
          const cx = x(b.time + periodMs / 2),
            upBar = b.close >= b.open;
          ctx.strokeStyle = ctx.fillStyle = upBar ? C.up : C.down;
          ctx.beginPath();
          ctx.moveTo(cx, y(b.high));
          ctx.lineTo(cx, y(b.low));
          ctx.stroke();
          ctx.fillRect(cx - bw / 2, Math.min(y(b.open), y(b.close)), bw, Math.max(1, Math.abs(y(b.open) - y(b.close))));
        }
      }
      // preview of where a new trade would close
      if (previewExpiry < tMax && trades.every((t) => Math.abs(x(t.expiresAtMs) - x(previewExpiry)) > 8)) {
        ctx.setLineDash([2, 4]);
        ctx.strokeStyle = C.hint;
        ctx.beginPath();
        ctx.moveTo(x(previewExpiry), top);
        ctx.lineTo(x(previewExpiry), top + plotH);
        ctx.stroke();
        ctx.setLineDash([]);
        ctx.fillStyle = C.hint;
        ctx.textAlign = 'center';
        ctx.fillText('финиш', x(previewExpiry), top + plotH - 8);
        ctx.textAlign = 'left';
      }
      // open trades: entry line, entry dot, stake label, expiry line with countdown
      const yLive = y(quote.price),
        taken: number[] = [yLive],
        pills: number[] = [];
      for (const t of trades) {
        const entry = t.openPrice as number,
          winning = quote.price !== entry && (quote.price > entry) === (t.direction === 'up');
        const color = quote.price === entry ? C.hint : winning ? C.up : C.down;
        const x0 = x(t.openQuote!.atMs),
          x1 = Math.min(x(t.expiresAtMs), plotW),
          yy = y(entry);
        // "Capsule" of the experiment: from entry to expiry, wrapping entry and live price, in the
        // current result colour. It flares up for a moment when the price crosses the entry.
        const yq = y(quote.price),
          lit = performance.now() - flashAt < 600,
          cTop = Math.min(yy, yq) - 7,
          cH = Math.abs(yq - yy) + 14;
        ctx.beginPath();
        ctx.roundRect(x0, cTop, Math.max(14, x1 - x0), cH, Math.min(10, cH / 2));
        const fill = withAlpha(ctx, color, lit ? 0.32 : 0.12),
          edge = withAlpha(ctx, color, lit ? 0.9 : 0.5);
        ctx.fillStyle = fill;
        ctx.shadowColor = color;
        ctx.shadowBlur = lit ? 20 : 8;
        ctx.fill();
        ctx.shadowBlur = 0;
        ctx.strokeStyle = edge;
        ctx.lineWidth = 1;
        ctx.stroke();
        ctx.strokeStyle = color;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(x0, yy);
        ctx.lineTo(x1, yy);
        ctx.stroke();
        ctx.lineWidth = 1;
        ctx.setLineDash([4, 3]);
        ctx.beginPath();
        ctx.moveTo(x1, top);
        ctx.lineTo(x1, top + plotH);
        ctx.stroke();
        ctx.setLineDash([]);
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.arc(x0, yy, 4, 0, Math.PI * 2);
        ctx.fill();
        const label = `${t.direction === 'up' ? '↑' : '↓'} ${money(t.stakeCents)} Д`;
        const lw = ctx.measureText(label).width + 14,
          lx = Math.max(2, x0 - lw - 8);
        let ly = yy;
        for (let i = 0; i < 12 && taken.some((o) => Math.abs(o - ly) < 22); i++) ly += (t.direction === 'up' ? -1 : 1) * 11 * (i % 2 ? -1 : 1) * (i + 1);
        taken.push(ly);
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.roundRect(lx, ly - 10, lw, 20, 10);
        ctx.fill();
        ctx.fillStyle = '#fff';
        ctx.fillText(label, lx + 7, ly);
        if (pills.some((px) => Math.abs(px - x1) < 40)) continue;
        pills.push(x1);
        const cd = t.expiresAtMs > now ? countdown(t.expiresAtMs - now) : '…';
        const cw = ctx.measureText(cd).width + 12;
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.roundRect(Math.min(x1 - cw / 2, plotW - cw), top, cw, 18, 9);
        ctx.fill();
        ctx.fillStyle = '#fff';
        ctx.fillText(cd, Math.min(x1 - cw / 2, plotW - cw) + 6, top + 9);
      }
      // live price
      const yq = y(quote.price);
      ctx.strokeStyle = C.accent;
      ctx.setLineDash([3, 3]);
      ctx.beginPath();
      ctx.moveTo(0, yq);
      ctx.lineTo(plotW, yq);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.fillStyle = C.accent;
      ctx.beginPath();
      ctx.roundRect(plotW + 2, yq - 10, axis - 4, 20, 4);
      ctx.fill();
      ctx.fillStyle = '#fff';
      ctx.fillText(fmt(quote.price, asset), plotW + 7, yq);
      // The live point is an HTML element on top of the canvas, so its pulse animates smoothly
      // between redraws. With an open trade it takes the trade's current result colour.
      const d = dot.current;
      if (d) {
        const lead = trades[0];
        const tone = !lead || quote.price === lead.openPrice ? '' : (quote.price > (lead.openPrice as number)) === (lead.direction === 'up') ? 'win' : 'loss';
        d.className = `live-dot ${kind} ${lead ? 'trading' : ''} ${tone}`;
        d.style.transform = `translate(${canvas.offsetLeft + x(now)}px, ${canvas.offsetTop + yq}px)`;
      }
    };
    draw();
    const ro = new ResizeObserver(draw);
    ro.observe(canvas);
    return () => ro.disconnect();
  }, [s, asset, now, previewExpiry, kind, flashAt]);
  return (
    <>
      <canvas
        ref={ref}
        className="chart-canvas"
        role="img"
        aria-label={`График ${asset.name}${kind === 'line' ? ' (линия)' : ''}, текущая цена ${s.quote ? fmt(s.quote.price, asset) : 'недоступна'}`}
      />
      <div ref={dot} className="live-dot" aria-hidden />
    </>
  );
}

function Sheet({ title, open, onClose, children }: { title: string; open: boolean; onClose: () => void; children: React.ReactNode }) {
  // stays on screen for the closing animation after `open` turns false
  const [shown, setShown] = useState(open),
    [leaving, setLeaving] = useState(false);
  useEffect(() => {
    if (open) {
      setShown(true);
      setLeaving(false);
      return;
    }
    setLeaving(true);
    const id = setTimeout(() => (setShown(false), setLeaving(false)), 180);
    return () => clearTimeout(id);
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const k = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [open, onClose]);
  if (!shown) return null;
  return (
    <div className={`sheet-layer ${leaving ? 'leaving' : ''}`} onClick={onClose}>
      <section className="sheet" role="dialog" aria-modal="true" aria-label={title} onClick={(e) => e.stopPropagation()}>
        <header className="sheet-head">
          <h2>{title}</h2>
          <button className="icon-btn" onClick={onClose} aria-label="Закрыть">✕</button>
        </header>
        <div className="sheet-body">{children}</div>
      </section>
    </div>
  );
}

// Price path of a settled trade: 1-second closes before entry (muted) and from entry to close,
// drawn in the result colour. Remounting (new key) replays the drawing animation.
function ReplayChart({ data, asset, tone }: { data: Replay; asset: Asset; tone: string }) {
  const { entry, close } = data;
  const before = data.points.filter((p) => p.atMs < entry.atMs);
  const during = [{ atMs: entry.atMs, price: entry.price }, ...data.points.filter((p) => p.atMs > entry.atMs && p.atMs < close.atMs), { atMs: close.atMs, price: close.price }];
  const all = [...before, ...during];
  const t0 = Math.min(...all.map((p) => p.atMs)),
    t1 = Math.max(data.expiresAtMs, close.atMs);
  let lo = Math.min(...all.map((p) => p.price)),
    hi = Math.max(...all.map((p) => p.price));
  const pad = Math.max((hi - lo) * 0.15, 1);
  lo -= pad;
  hi += pad;
  const W = 300,
    H = 120;
  const x = (t: number) => 6 + ((t - t0) / Math.max(1, t1 - t0)) * (W - 12),
    y = (p: number) => 6 + ((hi - p) / (hi - lo)) * (H - 12);
  const line = (pts: { atMs: number; price: number }[]) => pts.map((p) => `${x(p.atMs).toFixed(1)},${y(p.price).toFixed(1)}`).join(' ');
  return (
    <svg className={`replay ${tone}`} viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`Путь цены ${asset.name}: вход ${fmt(entry.price, asset)}, выход ${fmt(close.price, asset)}`}>
      <line className="replay-entry" x1={0} x2={W} y1={y(entry.price)} y2={y(entry.price)} />
      <line className="replay-expiry" x1={x(data.expiresAtMs)} x2={x(data.expiresAtMs)} y1={0} y2={H} />
      {before.length > 1 && <polyline className="replay-before" points={line([...before, during[0]])} />}
      <polyline className="replay-line" pathLength={1} points={line(during)} />
      <circle className="replay-dot-in" cx={x(entry.atMs)} cy={y(entry.price)} r={4} />
      <circle className="replay-dot-out" cx={x(close.atMs)} cy={y(close.price)} r={4} />
    </svg>
  );
}

// What Binance itself says about one trade, fetched from the user's device (not through our server).
type Check = { ok: boolean; id: string; price: number | null; atMs: number | null; problems: string[] };

// Exchange decimal string → integer price units, without floating point (same rule as the server).
const toUnits = (p: unknown, a: Asset) => {
  if (typeof p !== 'string' || !/^\d+(\.\d+)?$/.test(p)) return null;
  const [whole, fraction = ''] = p.split('.');
  return Number(whole) * a.scale + Number(fraction.slice(0, a.digits).padEnd(a.digits, '0'));
};

async function checkOnBinance(q: Quote, a: Asset): Promise<Check> {
  const r = await fetch(verifyUrl(q), { cache: 'no-store', signal: AbortSignal.timeout(10_000) });
  if (!r.ok) throw new Error(`Binance ответил ошибкой ${r.status}`);
  const row = ((await r.json()) as { a?: number; p?: string; T?: number }[])[0];
  const id = row ? String(row.a) : '—',
    price = row ? toUnits(row.p, a) : null,
    atMs = row && Number.isSafeInteger(row.T) ? (row.T as number) : null;
  const problems = [
    ...(id !== q.tradeId ? [`номер сделки ${id}, в чеке ${q.tradeId}`] : []),
    ...(price !== q.price ? [`цена ${price === null ? '—' : fmt(price, a)}, в чеке ${fmt(q.price, a)}`] : []),
    ...(atMs !== q.atMs ? [`время ${atMs === null ? '—' : clockMs(atMs)}, в чеке ${clockMs(q.atMs)}`] : []),
  ];
  return { ok: problems.length === 0, id, price, atMs, problems };
}

function QuoteRow({ label, q, asset, check }: { label: string; q: Quote; asset: Asset; check?: Check }) {
  return (
    <div className="r-row">
      <div className="r-row-main">
        <span>{label}</span>
        <strong>{fmt(q.price, asset)}</strong>
      </div>
      <div className="r-row-proof">
        <small>
          {clockMs(q.atMs)} · №{q.tradeId}
        </small>
        <button className="link-btn raw" onClick={() => openExternal(verifyUrl(q))}>
          API Binance ↗
        </button>
      </div>
      {check &&
        (check.ok ? (
          <p className="r-check ok">
            ✓ Binance: сделка №{check.id}, {fmt(check.price as number, asset)}, {clockMs(check.atMs as number)} — совпадает
          </p>
        ) : (
          <p className="r-check bad">✕ Не совпадает с Binance: {check.problems.join('; ')}</p>
        ))}
    </div>
  );
}

// "Trade receipt": result, replay, the two exact Binance trades with links to check them, and the odds.
function Receipt({ s, t, onNew }: { s: Snapshot; t: Trade; onNew: () => void }) {
  const asset = s.assets.find((a) => a.id === t.assetId)!;
  const [data, setData] = useState<Replay | null>(null),
    [problem, setProblem] = useState(''),
    [run, setRun] = useState(0),
    [verify, setVerify] = useState<{ state: 'idle' | 'busy' | 'done' | 'error'; entry?: Check; close?: Check; error?: string }>({ state: 'idle' });
  const settled = !!t.openQuote && !!t.closeQuote;
  useEffect(() => setVerify({ state: 'idle' }), [t.id]);
  const runVerify = async () => {
    if (!t.openQuote || !t.closeQuote) return;
    setVerify({ state: 'busy' });
    try {
      const [entry, close] = await Promise.all([checkOnBinance(t.openQuote, asset), checkOnBinance(t.closeQuote, asset)]);
      setVerify({ state: 'done', entry, close });
      haptic(entry.ok && close.ok ? 'success' : 'error');
    } catch (e) {
      setVerify({ state: 'error', error: e instanceof Error && e.name !== 'TimeoutError' ? e.message : 'Binance не ответил вовремя' });
    }
  };
  useEffect(() => {
    if (!settled) return;
    let off = false;
    setData(null);
    setProblem('');
    fetch(`/api/trades/${encodeURIComponent(t.id)}/replay`, { cache: 'no-store', headers: auth(), signal: AbortSignal.timeout(10_000) })
      .then(async (r) => {
        const reply = await r.json();
        if (!r.ok) throw new Error(reply.error?.message || 'Реплей сейчас недоступен.');
        return reply as Replay;
      })
      .then(
        (d) => !off && setData(d),
        (e) => !off && setProblem(e instanceof Error ? e.message : 'Реплей сейчас недоступен.'),
      );
    return () => {
      off = true;
    };
  }, [t.id, settled]);
  const tone = t.status === 'win' ? 'win' : t.status === 'loss' ? 'loss' : 'flat';
  const title = t.status === 'win' ? 'Прибыль' : t.status === 'loss' ? 'Убыток' : t.status === 'tie' ? 'В ноль' : 'Возврат';
  const amount = t.status === 'win' ? `+${money((t.payoutCents ?? 0) - t.stakeCents)} Д` : t.status === 'loss' ? `−${money(t.stakeCents)} Д` : `${money(t.stakeCents)} Д вернулись`;
  const dir = t.direction === 'up' ? 'Выше' : 'Ниже';
  const term = t.durationMs ? durationLabel(t.durationMs / 1000) : `до ${clock(t.expiresAtMs, false)}`;
  const [sharing, setSharing] = useState(false);
  const share = () => {
    if (!['win', 'loss', 'tie'].includes(t.status) || !t.openQuote || !t.closeQuote) return shareText();
    const [entry, close] = [t.openQuote, t.closeQuote];
    setSharing(true);
    void sharePicture(
      'trade',
      t.id,
      async () => ({ tone, asset, title, amount, detail: `${dir} · ${money(t.stakeCents)} Д · ${term}`, entry, close, points: data?.points ?? [], expiresAtMs: t.expiresAtMs, bot: s.botUsername }),
      shareText,
    ).finally(() => setSharing(false));
  };
  const shareText = () => {
    const text =
      `${title} ${amount} в Direction Lab: ${asset.name}, ${dir.toLowerCase()}, ${term}.` +
      (t.openQuote && t.closeQuote
        ? ` Вход ${fmt(t.openQuote.price, asset)} (сделка Binance №${t.openQuote.tradeId}), выход ${fmt(t.closeQuote.price, asset)} (№${t.closeQuote.tradeId}). Каждую цену можно проверить на Binance.`
        : '');
    const link = s.botUsername ? `https://t.me/${s.botUsername}` : window.location.origin;
    const url = `https://t.me/share/url?url=${encodeURIComponent(link)}&text=${encodeURIComponent(text)}`;
    const w = tg();
    if (w?.openTelegramLink) w.openTelegramLink(url);
    else window.open(url, '_blank', 'noopener');
  };
  return (
    <div className="receipt">
      <div className={`r-head ${tone}`}>
        <span className="r-mark" aria-hidden>{t.status === 'win' ? '✓' : t.status === 'loss' ? '✕' : '='}</span>
        <div>
          <strong>
            {title} <span className={tone}>{amount}</span>
          </strong>
          <small className="with-icon">
            <AssetIcon a={asset} small />
            {asset.name} · {dir} · {money(t.stakeCents)} Д · {term}
          </small>
        </div>
      </div>
      {settled ? (
        <>
          <div className="r-chart">
            {data ? <ReplayChart key={run} data={data} asset={asset} tone={tone} /> : <div className="r-chart-empty">{problem || 'Загружаем путь цены…'}</div>}
            {data && (
              <div className="r-chart-foot">
                <button className="r-replay" onClick={() => setRun((n) => n + 1)}>
                  ▶ Реплей
                </button>
                <small>пунктир — цена входа</small>
              </div>
            )}
          </div>
          <div className="r-rows">
            <QuoteRow label="Вход" q={t.openQuote!} asset={asset} check={verify.entry} />
            <QuoteRow label="Выход" q={t.closeQuote!} asset={asset} check={verify.close} />
          </div>
          <div className={`r-verify ${verify.state === 'done' ? (verify.entry?.ok && verify.close?.ok ? 'ok' : 'bad') : verify.state}`}>
            {verify.state === 'done' ? (
              <strong>{verify.entry?.ok && verify.close?.ok ? '✓ Binance подтверждает обе цены и время' : '✕ Данные Binance не совпали с чеком'}</strong>
            ) : verify.state === 'error' ? (
              <strong>Не удалось спросить Binance: {verify.error}.</strong>
            ) : (
              <button className="primary" disabled={verify.state === 'busy'} onClick={() => void runVerify()}>
                {verify.state === 'busy' ? 'Спрашиваем Binance…' : 'Проверить на Binance'}
              </button>
            )}
            <small>
              {verify.state === 'error' ? (
                <button className="link-btn" onClick={() => void runVerify()}>Попробовать ещё раз</button>
              ) : (
                'Запрос идёт с вашего устройства прямо на Binance, мимо нашего сервера, поэтому подменить ответ мы не можем.'
              )}
            </small>
          </div>
          <p className="fine">Выход — последняя сделка на бирже до {clock(t.expiresAtMs)}. Линия — секундные свечи Binance, точки входа и выхода — те самые сделки.</p>
        </>
      ) : (
        <p className="fine">{tradeState(t, Date.now(), null).label}.</p>
      )}
      <p className="r-fair">
        Честно о шансах: при доходности {t.payoutBps / 100}% в ноль выходит трейдер, у которого верны {breakEven(t.payoutBps)}% прогнозов.
      </p>
      <div className="r-actions">
        <button className="secondary" disabled={sharing} onClick={share}>{sharing ? 'Готовим картинку…' : 'Поделиться'}</button>
        <button className="primary" onClick={onNew}>Ещё сделка</button>
      </div>
    </div>
  );
}

const dayMonth = (ms: number) => new Intl.DateTimeFormat('ru-RU', { timeZone: 'Europe/Moscow', day: 'numeric', month: 'long' }).format(ms);

// Weekly ranking plus the player's invite link. Refreshed while open.
const sideWord = (side: 'up' | 'down') => (side === 'up' ? 'Выше' : 'Ниже');
const opposite = (side: 'up' | 'down'): 'up' | 'down' => (side === 'up' ? 'down' : 'up');
const DUEL_STAKES = [100, 500, 1_000, 5_000, 10_000, 50_000, 100_000]; // 1 … 1 000 Д
const duelWin = (d: { stakeCents: number; feeBps: number }) => d.stakeCents - Math.floor((d.stakeCents * d.feeBps) / 10_000);
async function postJson(path: string, body: Record<string, unknown>) {
  const r = await fetch(path, { method: 'POST', headers: { 'Content-Type': 'application/json', ...auth() }, body: JSON.stringify(body), signal: AbortSignal.timeout(12_000) });
  const reply = await r.json();
  if (!r.ok) throw new Error(reply.error?.message || 'Не получилось. Попробуйте ещё раз.');
  return reply;
}
// ---- "Share as a picture": the result drawn on a canvas, sent through the bot as a photo with a button.
type Point = { atMs: number; price: number };
type Card = { tone: string; asset: Asset; title: string; amount: string; detail: string; entry: Quote; close: Quote; points: Point[]; expiresAtMs: number; bot: string | null };

// One-second Binance candles around a short period, straight from the exchange (duels have no replay route).
async function binancePath(a: Asset, fromMs: number, toMs: number): Promise<Point[]> {
  const r = await fetch(`https://data-api.binance.vision/api/v3/klines?symbol=${a.id}&interval=1s&startTime=${fromMs}&endTime=${toMs}&limit=1000`, { signal: AbortSignal.timeout(8_000) });
  if (!r.ok) return [];
  return ((await r.json()) as [number, string, string, string, string][]).flatMap((k) => {
    const price = toUnits(k[4], a);
    return price === null ? [] : [{ atMs: k[0], price }];
  });
}

const loadImage = (src: string) =>
  new Promise<HTMLImageElement | null>((done) => {
    const img = new Image();
    const t = setTimeout(() => done(null), 3_000);
    img.onload = () => (clearTimeout(t), done(img));
    img.onerror = () => (clearTimeout(t), done(null));
    img.src = src;
  });

// 1200×900 JPEG in the terminal's lab look: dark grid, the glowing price trace, the two Binance trades.
async function renderCard(c: Card) {
  const W = 1200,
    H = 900,
    P = 64;
  const cv = document.createElement('canvas');
  cv.width = W;
  cv.height = H;
  const ctx = cv.getContext('2d')!;
  const sans = 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
    mono = 'ui-monospace, "SF Mono", Menlo, Consolas, monospace';
  const ink = '#e8eef5',
    dim = '#7d8b9a',
    color = c.tone === 'win' ? '#2bd47a' : c.tone === 'loss' ? '#ff5a60' : '#9fb0c2';
  const bg = ctx.createLinearGradient(0, 0, W, H);
  bg.addColorStop(0, '#0d141c');
  bg.addColorStop(1, '#070a0e');
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, H);
  const glow = ctx.createRadialGradient(W - 160, 140, 0, W - 160, 140, 520);
  glow.addColorStop(0, withAlpha(ctx, color, 0.16));
  glow.addColorStop(1, 'rgba(0, 0, 0, 0)');
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, W, H);
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.035)';
  ctx.lineWidth = 1;
  ctx.beginPath();
  for (let x = P; x < W; x += 48) (ctx.moveTo(x + 0.5, 0), ctx.lineTo(x + 0.5, H));
  for (let y = 24; y < H; y += 48) (ctx.moveTo(0, y + 0.5), ctx.lineTo(W, y + 0.5));
  ctx.stroke();

  ctx.fillStyle = ink;
  ctx.font = `700 30px ${mono}`;
  ctx.fillText('DIRECTION LAB', P, 92);
  ctx.fillStyle = dim;
  ctx.font = `500 24px ${mono}`;
  ctx.textAlign = 'right';
  ctx.fillText('ДЕМО · ЦЕНЫ BINANCE', W - P, 92);
  ctx.textAlign = 'left';

  const logo = c.asset.icon ? await loadImage(c.asset.icon) : null;
  if (logo) ctx.drawImage(logo, P, 140, 84, 84);
  else {
    ctx.fillStyle = 'rgba(255, 255, 255, 0.08)';
    ctx.beginPath();
    ctx.arc(P + 42, 182, 42, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = ink;
    ctx.font = `700 40px ${sans}`;
    ctx.textAlign = 'center';
    ctx.fillText(c.asset.symbol, P + 42, 196);
    ctx.textAlign = 'left';
  }
  ctx.fillStyle = ink;
  ctx.font = `700 44px ${sans}`;
  ctx.fillText(c.asset.name, P + 112, 178);
  ctx.fillStyle = dim;
  ctx.font = `500 28px ${sans}`;
  ctx.fillText(c.detail, P + 112, 220);

  ctx.fillStyle = dim;
  ctx.font = `600 34px ${sans}`;
  ctx.fillText(c.title, P, 310, W - 2 * P);
  ctx.fillStyle = color;
  ctx.font = `800 104px ${sans}`;
  ctx.fillText(c.amount, P - 4, 412, W - 2 * P);

  // the trace: grey before the entry, the result colour from entry to exit, a dashed entry level
  const top = 460,
    bottom = 690;
  const before = c.points.filter((p) => p.atMs < c.entry.atMs);
  const during: Point[] = [{ atMs: c.entry.atMs, price: c.entry.price }, ...c.points.filter((p) => p.atMs > c.entry.atMs && p.atMs < c.close.atMs), { atMs: c.close.atMs, price: c.close.price }];
  const all = [...before, ...during];
  const t0 = Math.min(...all.map((p) => p.atMs)),
    t1 = Math.max(c.expiresAtMs, c.close.atMs);
  let lo = Math.min(...all.map((p) => p.price)),
    hi = Math.max(...all.map((p) => p.price));
  const pad = Math.max((hi - lo) * 0.18, 1);
  lo -= pad;
  hi += pad;
  const x = (t: number) => P + ((t - t0) / Math.max(1, t1 - t0)) * (W - 2 * P),
    y = (p: number) => top + ((hi - p) / (hi - lo)) * (bottom - top);
  const path = (list: Point[]) => {
    ctx.beginPath();
    list.forEach((p, i) => (i ? ctx.lineTo(x(p.atMs), y(p.price)) : ctx.moveTo(x(p.atMs), y(p.price))));
  };
  ctx.setLineDash([10, 10]);
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.28)';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(P, y(c.entry.price));
  ctx.lineTo(W - P, y(c.entry.price));
  ctx.stroke();
  ctx.setLineDash([]);
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';
  if (before.length) {
    path([...before, during[0]]);
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.25)';
    ctx.lineWidth = 3;
    ctx.stroke();
  }
  path(during);
  ctx.lineTo(x(c.close.atMs), bottom);
  ctx.lineTo(x(c.entry.atMs), bottom);
  ctx.closePath();
  const fill = ctx.createLinearGradient(0, top, 0, bottom);
  fill.addColorStop(0, withAlpha(ctx, color, 0.22));
  fill.addColorStop(1, withAlpha(ctx, color, 0));
  ctx.fillStyle = fill;
  ctx.fill();
  path(during);
  ctx.shadowColor = color;
  ctx.shadowBlur = 18;
  ctx.strokeStyle = color;
  ctx.lineWidth = 5;
  ctx.stroke();
  ctx.shadowBlur = 0;
  for (const [q, r, fillColor] of [[c.entry, 9, ink], [c.close, 12, color]] as const) {
    ctx.fillStyle = fillColor;
    ctx.beginPath();
    ctx.arc(x(q.atMs), y(q.price), r, 0, Math.PI * 2);
    ctx.fill();
  }

  // the two exchange trades anyone can look up
  const quote = (label: string, q: Quote, at: number, align: CanvasTextAlign) => {
    ctx.textAlign = align;
    ctx.fillStyle = dim;
    ctx.font = `600 22px ${mono}`;
    ctx.fillText(label, at, 752);
    ctx.fillStyle = ink;
    ctx.font = `700 40px ${mono}`;
    ctx.fillText(fmt(q.price, c.asset), at, 800);
    ctx.fillStyle = dim;
    ctx.font = `500 22px ${mono}`;
    ctx.fillText(`Binance №${q.tradeId} · ${clock(q.atMs)}`, at, 834);
  };
  quote('ВХОД', c.entry, P, 'left');
  quote('ВЫХОД', c.close, W - P, 'right');
  ctx.textAlign = 'center';
  ctx.fillStyle = dim;
  ctx.font = `500 22px ${sans}`;
  ctx.fillText(`Каждая цена — настоящая сделка Binance${c.bot ? ` · t.me/${c.bot}` : ''}`, W / 2, 878);
  return cv.toDataURL('image/jpeg', 0.88);
}

// Telegram 8.0+: draw the card, let the bot prepare it, open the chat picker. Anything else → the text share.
async function sharePicture(kind: 'trade' | 'duel', id: string, card: () => Promise<Card>, fallback: () => void) {
  const w = tg();
  if (!w?.shareMessage || !w.isVersionAtLeast?.('8.0')) return fallback();
  try {
    const { preparedId } = await postJson('/api/share', { kind, id, image: await renderCard(await card()) });
    w.shareMessage(preparedId, (sent) => sent && haptic('success'));
  } catch {
    fallback();
  }
}

const duelLink = (id: string, bot: string | null) => (bot ? `https://t.me/${bot}?start=duel_${id}` : `${window.location.origin}/?duel=${id}`);
function shareDuel(d: Duel, a: Asset, bot: string | null) {
  const text = `⚔️ Вызываю на дуэль: ${a.name}, ${whole(d.stakeCents)} Д, 1 минута. Я за «${sideWord(d.creator.side)}», ты — за «${sideWord(opposite(d.creator.side))}». Чей прогноз верный — забирает обе суммы. Цены — реальные сделки Binance.`;
  const url = `https://t.me/share/url?url=${encodeURIComponent(duelLink(d.id, bot))}&text=${encodeURIComponent(text)}`;
  const w = tg();
  if (w?.openTelegramLink) w.openTelegramLink(url);
  else window.open(url, '_blank', 'noopener');
}

// One duel, as the viewer sees it: their side, the rival, and what to do next.
function DuelCard({ d, a, price, now, bot, busy, onAccept, onCancel }: { d: Duel; a: Asset; price: number | null; now: number; bot: string | null; busy: string; onAccept: (id: string) => void; onCancel: (id: string) => void }) {
  const mySide = d.you === 'creator' ? d.creator.side : d.you === 'opponent' ? d.opponent!.side : opposite(d.creator.side);
  const rival = d.you === 'creator' ? d.opponent?.name : d.creator.name;
  const lead = d.status === 'live' && price !== null && d.openPrice !== null ? Math.sign(price - d.openPrice) * (mySide === 'up' ? 1 : -1) : 0;
  const won = d.status === 'won' && d.winner === d.you;
  const tone = d.status === 'won' ? (won ? 'win' : 'loss') : d.status === 'live' ? (lead > 0 ? 'win' : lead < 0 ? 'loss' : 'flat') : 'flat';
  const [sharing, setSharing] = useState(false);
  const shareResult = () => {
    const [entry, close] = [d.openQuote!, d.closeQuote!];
    const title = d.status === 'tie' ? `Ничья в дуэли с ${rival}` : won ? `Победа в дуэли над ${rival}` : `Дуэль с ${rival}`;
    const amount = d.status === 'tie' ? '±0 Д' : won ? `+${amt(d.stakeCents - (d.feeCents ?? 0))} Д` : `−${whole(d.stakeCents)} Д`;
    const asText = () => {
      const text = `⚔️ ${title}: ${amount}. ${a.name}, одна минута, цены — реальные сделки Binance. Вызови меня на реванш!`;
      const url = `https://t.me/share/url?url=${encodeURIComponent(bot ? `https://t.me/${bot}` : window.location.origin)}&text=${encodeURIComponent(text)}`;
      const w = tg();
      if (w?.openTelegramLink) w.openTelegramLink(url);
      else window.open(url, '_blank', 'noopener');
    };
    setSharing(true);
    void sharePicture(
      'duel',
      d.id,
      async () => ({
        tone, asset: a, title: `⚔️ ${title}`, amount, detail: `за «${sideWord(mySide)}» · ${whole(d.stakeCents)} Д · 1 мин`, entry, close,
        points: await binancePath(a, entry.atMs - 5_000, close.atMs + 1_000).catch(() => []), expiresAtMs: d.endsAtMs ?? close.atMs, bot,
      }),
      asText,
    ).finally(() => setSharing(false));
  };
  return (
    <li className={`duel t-${tone}`}>
      <div className="duel-head">
        <span className="with-icon">
          <AssetIcon a={a} small />
          <strong>{a.name}</strong>
        </span>
        <span className="duel-stake">{whole(d.stakeCents)} Д</span>
        <span className={`duel-side ${mySide}`}>{d.you ? 'вы' : 'вы будете'} за «{sideWord(mySide)}»</span>
      </div>
      {d.status === 'open' && d.you === 'creator' && (
        <>
          <p className="duel-line">
            Ждём соперника · <b>{countdown(d.acceptUntilMs - now)}</b> {d.visibility === 'link' ? '· только по ссылке' : '· в лобби'}
          </p>
          <div className="duel-actions">
            <button className="primary" onClick={() => shareDuel(d, a, bot)}>Отправить другу</button>
            <button className="secondary" disabled={!!busy} onClick={() => onCancel(d.id)}>Отменить</button>
          </div>
        </>
      )}
      {d.status === 'open' && d.you !== 'creator' && (
        <>
          <p className="duel-line">
            {d.creator.name} за «{sideWord(d.creator.side)}». Прогноз верный — <b className="win">+{amt(duelWin(d))} Д</b>, нет — −{whole(d.stakeCents)} Д.
          </p>
          <button className={`go-btn ${mySide}`} disabled={!!busy} onClick={() => onAccept(d.id)}>
            {busy === d.id ? 'Принимаем…' : `Принять за «${sideWord(mySide)}»`}
          </button>
        </>
      )}
      {d.status === 'pending' && <p className="duel-line">Против {rival} · фиксируем цену старта…</p>}
      {d.status === 'live' && (
        <p className="duel-line">
          Против {rival} · <b>{countdown((d.endsAtMs ?? now) - now)}</b> · {fmt(d.openPrice!, a)} → {price !== null ? fmt(price, a) : '…'} ·{' '}
          <b className={lead > 0 ? 'win' : lead < 0 ? 'loss' : ''}>{lead > 0 ? 'вы ведёте' : lead < 0 ? 'ведёт соперник' : 'пока ровно'}</b>
        </p>
      )}
      {['won', 'tie', 'void', 'expired', 'cancelled'].includes(d.status) && (
        <p className="duel-line">
          {d.status === 'won' ? (
            won ? (
              <b className="win">Победа над {rival}: +{amt(d.stakeCents - (d.feeCents ?? 0))} Д</b>
            ) : (
              <b className="loss">Поражение от {rival}: −{whole(d.stakeCents)} Д</b>
            )
          ) : d.status === 'tie' ? (
            `Ничья с ${rival}: цена не изменилась, суммы вернулись`
          ) : d.status === 'void' ? (
            'Отменена: биржа не подтвердила цену, суммы вернулись'
          ) : d.status === 'expired' ? (
            'Никто не принял вызов — сумма вернулась'
          ) : (
            'Вызов отменён — сумма вернулась'
          )}
        </p>
      )}
      {d.you && ['won', 'tie'].includes(d.status) && d.openQuote && d.closeQuote && (
        <button className="secondary duel-share" disabled={sharing} onClick={shareResult}>
          {sharing ? 'Готовим картинку…' : won ? 'Похвастаться' : 'Поделиться'}
        </button>
      )}
      {d.openQuote && d.closeQuote && (
        <details className="duel-proof">
          <summary>Цены и проверка на Binance</summary>
          <QuoteRow label="Старт" q={d.openQuote} asset={a} />
          <QuoteRow label="Финиш" q={d.closeQuote} asset={a} />
        </details>
      )}
    </li>
  );
}

// Duels: challenge someone (lobby or by link), accept a challenge, follow your duels.
function Duels({ s, focusId }: { s: Snapshot; focusId: string }) {
  const [b, setB] = useState<DuelBoard | null>(null),
    [problem, setProblem] = useState(''),
    [error, setError] = useState(''),
    [busy, setBusy] = useState(''),
    [focus, setFocus] = useState<Duel | null>(null),
    [, setTick] = useState(0);
  const [form, setForm] = useState<{ assetId: string; side: 'up' | 'down'; stakeCents: number; visibility: 'lobby' | 'link' }>({ assetId: 'BTCUSDT', side: 'up', stakeCents: 1_000, visibility: 'lobby' });
  const got = useRef(0);
  const load = useCallback(async () => {
    try {
      const r = await fetch('/api/duels', { cache: 'no-store', headers: auth(), signal: AbortSignal.timeout(10_000) });
      const reply = await r.json();
      if (!r.ok) throw new Error(reply.error?.message || 'Дуэли сейчас недоступны.');
      got.current = performance.now();
      setB(reply as DuelBoard);
      setProblem('');
    } catch (e) {
      setProblem(e instanceof Error ? e.message : 'Дуэли сейчас недоступны.');
    }
  }, []);
  useEffect(() => {
    void load();
    const poll = setInterval(load, 2000),
      clock = setInterval(() => setTick((n) => n + 1), 250);
    return () => {
      clearInterval(poll);
      clearInterval(clock);
    };
  }, [load]);
  useEffect(() => {
    if (!focusId) return;
    fetch(`/api/duels/${encodeURIComponent(focusId)}`, { cache: 'no-store', headers: auth() })
      .then((r) => r.json())
      .then((reply) => setFocus(reply.duel ?? null))
      .catch(() => setFocus(null));
  }, [focusId]);
  if (!b) return <p className="empty">{problem || 'Загружаем дуэли…'}</p>;
  const now = b.serverNowMs + (performance.now() - got.current);
  const assetOf = (id: string) => s.assets.find((a) => a.id === id) ?? s.assets[0];
  const act = async (key: string, fn: () => Promise<unknown>) => {
    setBusy(key);
    setError('');
    try {
      await fn();
      haptic('impact');
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не получилось.');
      haptic('error');
    } finally {
      setBusy('');
    }
  };
  const accept = (id: string) => act(id, () => postJson(`/api/duels/${id}/accept`, {}).then(() => setFocus(null)));
  const cancel = (id: string) => act(id, () => postJson(`/api/duels/${id}/cancel`, {}));
  // the pair on screen: the chosen one, or the first open arena if it was closed meanwhile
  const arena = b.arenas.includes(form.assetId) ? form.assetId : b.arenas[0];
  const create = () => act('create', () => postJson('/api/duels', { id: crypto.randomUUID(), ...form, assetId: arena }));
  const maxStake = Math.min(b.rules.maxStakeCents, s.balanceCents);
  const card = (d: Duel) => <DuelCard key={d.id} d={d} a={assetOf(d.assetId)} price={b.prices[d.assetId] ?? null} now={now} bot={s.botUsername} busy={busy} onAccept={accept} onCancel={cancel} />;
  const running = b.mine.filter((d) => ['open', 'pending', 'live'].includes(d.status)),
    finished = b.mine.filter((d) => !['open', 'pending', 'live'].includes(d.status));
  return (
    <div className="duels">
      {error && <div className="notice bad" role="alert">{error}</div>}
      {focus && !focus.you && (
        <div className="duel-focus">
          <h3>Вам бросили вызов</h3>
          {focus.status === 'open' ? <ul className="duel-list">{card(focus)}</ul> : <p className="empty">Этот вызов уже принят или закрыт — бросьте свой.</p>}
        </div>
      )}
      <Guide pose="duel">
        Один на один, одна минута. Выбери пару и прогноз — чей прогноз верный, тот забирает обе суммы.
      </Guide>
      <h3>Бросить вызов</h3>
      <div className="duel-form">
        <div className="chips arenas">
          {b.arenas.map((id, i) => {
            const a = assetOf(id);
            return (
              <button key={id} className={arena === id ? 'on' : ''} onClick={() => setForm({ ...form, assetId: id })}>
                <AssetIcon a={a} small /> {a.name.replace(' / USDT', '')}
                {i >= 2 && <em>горячая</em>}
              </button>
            );
          })}
        </div>
        <div className="go">
          <button className={`go-btn up ${form.side === 'up' ? '' : 'dim'}`} onClick={() => setForm({ ...form, side: 'up' })}>↑ Выше</button>
          <button className={`go-btn down ${form.side === 'down' ? '' : 'dim'}`} onClick={() => setForm({ ...form, side: 'down' })}>↓ Ниже</button>
        </div>
        <div className="chips">
          {DUEL_STAKES.filter((c) => c <= b.rules.maxStakeCents).map((c) => (
            <button key={c} className={form.stakeCents === c ? 'on' : ''} disabled={c > maxStake} onClick={() => setForm({ ...form, stakeCents: c })}>
              {whole(c)} Д
            </button>
          ))}
        </div>
        <div className="segmented">
          <button className={form.visibility === 'lobby' ? 'on' : ''} onClick={() => setForm({ ...form, visibility: 'lobby' })}>В лобби для всех</button>
          <button className={form.visibility === 'link' ? 'on' : ''} onClick={() => setForm({ ...form, visibility: 'link' })}>Только по ссылке</button>
        </div>
        <p className="fine">
          Соперник вносит столько же на противоположный прогноз. Через минуту по цене Binance: прогноз верный — <b className="win">+{amt(duelWin({ stakeCents: form.stakeCents, feeBps: b.rules.feeBps }))} Д</b> (сумма соперника минус {b.rules.feeBps / 100}% комиссии), нет — −{whole(form.stakeCents)} Д. Не примут за {b.rules.acceptMs / 60_000} минут — сумма вернётся.
        </p>
        <button className="primary" disabled={!!busy || form.stakeCents > maxStake} onClick={() => void create()}>
          {busy === 'create' ? 'Создаём…' : `Бросить вызов на ${whole(form.stakeCents)} Д`}
        </button>
      </div>
      {running.length > 0 && (
        <>
          <h3>Мои дуэли</h3>
          <ul className="duel-list">{running.map(card)}</ul>
        </>
      )}
      <h3>Лобби</h3>
      {b.lobby.length ? <ul className="duel-list">{b.lobby.map(card)}</ul> : <p className="empty">Открытых вызовов пока нет — бросьте свой и отправьте другу.</p>}
      {finished.length > 0 && (
        <>
          <h3>Итоги за сутки</h3>
          <ul className="duel-list">{finished.map(card)}</ul>
        </>
      )}
    </div>
  );
}

function Leaderboard() {
  const [b, setB] = useState<Board | null>(null),
    [problem, setProblem] = useState('');
  useEffect(() => {
    let off = false;
    const load = () =>
      fetch('/api/leaderboard', { cache: 'no-store', headers: auth(), signal: AbortSignal.timeout(10_000) })
        .then(async (r) => {
          const reply = await r.json();
          if (!r.ok) throw new Error(reply.error?.message || 'Рейтинг сейчас недоступен.');
          if (!off) {
            setB(reply as Board);
            setProblem('');
          }
        })
        .catch((e) => !off && setProblem(e instanceof Error ? e.message : 'Рейтинг сейчас недоступен.'));
    void load();
    const id = setInterval(load, 15_000);
    return () => {
      off = true;
      clearInterval(id);
    };
  }, []);
  if (!b) return <p className="empty">{problem || 'Загружаем рейтинг…'}</p>;
  const bonus = `+${whole(b.referral.bonusCents)} Д`;
  const invite = () => {
    if (!b.inviteLink) return;
    const text = `Пробую демо-терминал «выше/ниже» на реальных ценах Binance — каждую цену можно проверить. Заходи: после первой сделки нам обоим дадут ${bonus}.`;
    const url = `https://t.me/share/url?url=${encodeURIComponent(b.inviteLink)}&text=${encodeURIComponent(text)}`;
    const w = tg();
    if (w?.openTelegramLink) w.openTelegramLink(url);
    else window.open(url, '_blank', 'noopener');
  };
  // the bull's line: where you stand and what it takes to climb
  const above = b.me.rank !== null ? b.top.find((r) => r.rank === (b.me.rank as number) - 1) : undefined;
  const hint =
    b.me.rank === 1
      ? 'Ты первый на этой неделе! Держи позицию до понедельника.'
      : b.me.rank !== null && above
        ? `Ты на ${b.me.rank}-м месте. До ${above.rank}-го — ${amt(above.netCents - b.me.netCents + 1)} Д.`
        : b.me.rank !== null
          ? `Ты на ${b.me.rank}-м месте. Продолжай в том же духе.`
          : b.minTrades > b.me.trades
            ? `Ещё ${b.minTrades - b.me.trades} ${plural(b.minTrades - b.me.trades, 'сделка', 'сделки', 'сделок')} — и ты в рейтинге недели.`
            : 'Ты пока за пределами топа — каждая прибыльная сделка поднимает выше.';
  return (
    <div className="board">
      <Guide pose="champion">{hint}</Guide>
      <p className="fine board-week">
        Неделя {dayMonth(b.fromMs)} — {dayMonth(b.toMs - 1)} · обнуляется в понедельник в 00:00 МСК
      </p>
      {b.top.length ? (
        <ol className="board-list">
          {b.top.map((r) => (
            <li key={r.rank} className={r.me ? 'me' : ''}>
              <span className="board-rank">{r.rank}</span>
              <span className="board-name">
                <span>
                  {r.name}
                  {r.ton && <span className="ton-badge" title="Кошелёк TON привязан"> 💎</span>}
                  {r.me && <em> · вы</em>}
                </span>
                <small>
                  {r.trades} сделок, {r.wins} в плюс
                </small>
              </span>
              <strong className={r.netCents > 0 ? 'win' : r.netCents < 0 ? 'loss' : ''}>{signed(r.netCents)} Д</strong>
            </li>
          ))}
        </ol>
      ) : (
        <p className="empty">На этой неделе ещё никто не сделал {b.minTrades} сделок — можно стать первым.</p>
      )}
      {b.me.rank === null && (
        <p className="fine">
          У вас {b.me.trades} из {b.minTrades} сделок, нужных для рейтинга. Итог недели: {signed(b.me.netCents)} Д.
        </p>
      )}
      <p className="fine">Считается чистый результат закрытых сделок за неделю: прибыль минус убытки. Пополнение демо-счёта на рейтинг не влияет.</p>
      {b.inviteLink && (
        <div className="invite">
          <strong>Пригласите друга</strong>
          <span>Когда друг сделает первую сделку, вы оба получите {bonus}.</span>
          <button className="primary" onClick={invite}>Пригласить друга</button>
          <small>
            Приглашено: {b.referral.invited}, уже сыграли: {b.referral.played}
            {b.referral.played ? ` (+${whole(b.referral.played * b.referral.bonusCents)} Д)` : ''}
          </small>
        </div>
      )}
    </div>
  );
}

function TradeList({ s, now, filter, setFilter, onExport, onReceipt }: { s: Snapshot; now: number; filter: string; setFilter: (f: string) => void; onExport: () => void; onReceipt: (id: string) => void }) {
  const list = s.trades.filter((t) => (filter === 'active' ? t.status === 'open' || t.status === 'pending' : t.status !== 'open' && t.status !== 'pending'));
  return (
    <div className="trades">
      <div className="segmented" role="tablist">
        <button role="tab" aria-selected={filter === 'active'} className={filter === 'active' ? 'on' : ''} onClick={() => setFilter('active')}>
          Открытые <b>{s.stats.open}</b>
        </button>
        <button role="tab" aria-selected={filter === 'closed'} className={filter === 'closed' ? 'on' : ''} onClick={() => setFilter('closed')}>
          История <b>{s.stats.closed}</b>
        </button>
      </div>
      {filter === 'closed' && s.stats.closed > 0 && (
        <p className="summary">
          {s.stats.wins} в плюс, {s.stats.losses} в минус, {s.stats.ties + s.stats.voids} возвратов. Итог{' '}
          <b className={s.stats.netCents > 0 ? 'win' : s.stats.netCents < 0 ? 'loss' : ''}>{signed(s.stats.netCents)} Д</b>
        </p>
      )}
      {!list.length && <p className="empty">{filter === 'active' ? 'Открытых сделок нет. Выберите направление, и сделка появится здесь и на графике.' : 'Закрытых сделок пока нет.'}</p>}
      <ul className="trade-list">
        {list.map((t) => {
          const a = s.assets.find((x) => x.id === t.assetId)!;
          const live = s.quote && s.quote.assetId === t.assetId ? s.quote.price : null;
          const st = tradeState(t, now, live);
          const total = t.expiresAtMs - (t.openQuote?.atMs ?? t.acceptedAtMs);
          const progress = t.status === 'open' ? Math.min(1, Math.max(0, 1 - (t.expiresAtMs - now) / total)) : 1;
          return (
            <li key={t.id} className={`trade t-${st.tone}`}>
              <div className="trade-main">
                <span className={`dir ${t.direction}`} aria-label={t.direction === 'up' ? 'Выше' : 'Ниже'}>{t.direction === 'up' ? '↑' : '↓'}</span>
                <div className="trade-what">
                  <strong className="with-icon">
                    <AssetIcon a={a} small />
                    {a.name}
                  </strong>
                  <small>
                    {money(t.stakeCents)} Д, {t.payoutBps / 100}%
                  </small>
                </div>
                <div className="trade-result">
                  <strong className={st.tone}>{st.label}</strong>
                  <small>
                    {t.openPrice !== null ? fmt(t.openPrice, a) : '…'} → {t.closePrice !== null ? fmt(t.closePrice, a) : live !== null && t.status === 'open' ? fmt(live, a) : '…'}
                  </small>
                </div>
              </div>
              {(t.status === 'open' || t.status === 'pending') && <div className="bar"><i style={{ width: `${progress * 100}%` }} /></div>}
              {t.status !== 'open' && t.status !== 'pending' && (
                <button className="link-btn proof" onClick={() => onReceipt(t.id)}>
                  Чек сделки{t.openQuote && t.closeQuote ? ' и реплей' : ''} →
                </button>
              )}
            </li>
          );
        })}
      </ul>
      <button className="link-btn" onClick={onExport}>Скачать полный журнал (JSON)</button>
    </div>
  );
}

const ONBOARDED = 'direction-lab-onboarded-v1';
// The bull guide: Telegram-sticker poses (assets/mascot has the sources).
type Pose = 'hello' | 'teach' | 'profit' | 'calm' | 'think' | 'duel' | 'wait' | 'champion';
function Mascot({ pose, size, className = '' }: { pose: Pose; size: number; className?: string }) {
  return <img className={`mascot ${className}`} src={`/mascot/${pose}.webp`} width={size} height={size} alt="" draggable={false} />;
}

// 1 сделка, 3 сделки, 5 сделок
const plural = (n: number, one: string, few: string, many: string) =>
  n % 10 === 1 && n % 100 !== 11 ? one : n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 12 || n % 100 > 14) ? few : many;

// The bull with a speech bubble: one short line where a trader needs a hint.
function Guide({ pose, children }: { pose: Pose; children: React.ReactNode }) {
  return (
    <div className="guide">
      <Mascot pose={pose} size={92} />
      <p className="guide-bubble">{children}</p>
    </div>
  );
}

const SLIDES: { title: string; text: string; pose: Pose }[] = [
  {
    title: 'Прогнозируй движение цены',
    text: 'Выбери пару и срок — от 15 секунд до 5 минут — и нажми «Выше» или «Ниже». Верный прогноз — до +85% к сумме сделки.',
    pose: 'hello',
  },
  {
    title: 'Цены настоящие, средства — демо',
    text: '10 000 Д на демо-счёте бесплатно, свои деньги не нужны. Каждая цена — реальная сделка Binance: открой чек и проверь сам.',
    pose: 'teach',
  },
  {
    title: 'Вызови друга на дуэль',
    text: 'Один на один, одна минута: чей прогноз верный — тот забирает обе суммы. Кнопка ⚔️ сверху, а в рейтинге недели видно лучших трейдеров.',
    pose: 'duel',
  },
];

// ---- TON wallet as part of the profile (TON Connect). No money: the wallet only signs our one-time
// phrase, which proves the wallet is the trader's; nothing is sent or charged. The TON Connect UI is
// a large library, so it is loaded only when the trader taps "connect".
type TonUi = import('@tonconnect/ui').TonConnectUI;
let tonUi: Promise<TonUi> | null = null;
const tonConnect = (bot: string | null) =>
  (tonUi ??= import('@tonconnect/ui').then(
    ({ TonConnectUI }) =>
      new TonConnectUI({
        manifestUrl: `${window.location.origin}/tonconnect-manifest.json`,
        language: 'ru',
        // after the wallet, come back to the terminal in Telegram
        ...(bot ? { actionsConfiguration: { twaReturnUrl: `https://t.me/${bot}?startapp` as `${string}://${string}` } } : {}),
      }),
  ));
const shortAddress = (a: string) => `${a.slice(0, 6)}…${a.slice(-6)}`;

function WalletLink({ s, onChange }: { s: Snapshot; onChange: () => void }) {
  const [busy, setBusy] = useState(false),
    [error, setError] = useState('');
  const connect = async () => {
    setBusy(true);
    setError('');
    try {
      const { payload } = await postJson('/api/wallet/challenge', {});
      const ui = await tonConnect(s.botUsername);
      if (ui.connected) await ui.disconnect().catch(() => {});
      ui.setConnectRequestParameters({ state: 'ready', value: { tonProof: payload } });
      const wallet = await new Promise<NonNullable<TonUi['wallet']>>((resolve, reject) => {
        const offStatus = ui.onStatusChange(
          (w) => {
            if (!w) return;
            offStatus();
            offModal();
            resolve(w);
          },
          (e) => {
            offStatus();
            offModal();
            reject(e);
          },
        );
        const offModal = ui.onModalStateChange((st) => {
          if (st.status === 'closed' && st.closeReason === 'action-cancelled') {
            offStatus();
            offModal();
            reject(new Error(''));
          }
        });
        void ui.openModal();
      });
      const item = wallet.connectItems?.tonProof;
      if (!item || !('proof' in item)) throw new Error('Кошелёк не прислал подтверждение. Попробуйте другой кошелёк.');
      await postJson('/api/wallet/link', { account: wallet.account, proof: item.proof });
      // the link lives on our side; no session with the wallet is kept
      await ui.disconnect().catch(() => {});
      haptic('success');
      onChange();
    } catch (e) {
      const message = e instanceof Error ? e.message : '';
      if (message) setError(message);
    } finally {
      setBusy(false);
    }
  };
  const unlink = async () => {
    setBusy(true);
    setError('');
    try {
      await postJson('/api/wallet/unlink', {});
      onChange();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не получилось. Попробуйте ещё раз.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="wallet">
      {s.wallet ? (
        <div className="wallet-row">
          <span className="wallet-mark" aria-hidden>💎</span>
          <span className="wallet-text">
            <b>{shortAddress(s.wallet.address)}</b>
            <small>Кошелёк привязан — в рейтинге рядом с вами значок 💎.</small>
          </span>
          <button className="link-btn" disabled={busy} onClick={() => void unlink()}>Отвязать</button>
        </div>
      ) : (
        <>
          <p className="fine">
            Привяжите свой кошелёк TON — в рейтинге появится значок 💎. Денег здесь нет: кошелёк только подписывает одноразовую фразу, это доказывает, что он ваш. Ничего не
            списывается, доступа к средствам у нас нет.
          </p>
          <button className="secondary" disabled={busy} onClick={() => void connect()}>
            {busy ? 'Ждём кошелёк…' : 'Подключить кошелёк TON'}
          </button>
        </>
      )}
      {error && <p className="notice bad">{error}</p>}
    </div>
  );
}

// Interactive tutorial: the bull walks a new trader through a real first trade, step by step.
// Each step lights up one part of the terminal (CSS, by body[data-coach=<step>]); the steps that
// need the trader's own action (the forecast) wait for it, waiting and the result follow the trade.
const TUTORIAL = 'direction-lab-tutorial-v1';
type CoachStep = 'pair' | 'time' | 'stake' | 'go' | 'wait' | 'result' | 'verify';
const COACH_ORDER: CoachStep[] = ['pair', 'time', 'stake', 'go', 'wait', 'result', 'verify'];
// milestones for the owner's funnel (best effort, never in the trader's way)
const track = (kind: string) => void postJson('/api/events', { kind }).catch(() => {});

function Coach({ step, trade, onNext, onSkip, onReceipt }: { step: CoachStep; trade: Trade | null; onNext: () => void; onSkip: () => void; onReceipt: () => void }) {
  const result = trade && trade.status !== 'open' && trade.status !== 'pending' ? trade : null;
  const lines: Record<CoachStep, { pose: Pose; text: string }> = {
    pair: { pose: 'hello', text: 'Привет! Покажу, как сделать первую сделку — это займёт минуту. Сверху пара: то, что мы торгуем. Начнём с BTC — биткоина к доллару.' },
    time: { pose: 'teach', text: 'Срок — через сколько сравним цену с ценой входа. Я поставил 1 минуту: за это время цена успеет сдвинуться.' },
    stake: { pose: 'teach', text: 'Сумма сделки — сколько демо-Д в деле. Для начала 10 Д. При верном прогнозе получишь +85% к сумме.' },
    go: { pose: 'think', text: 'Теперь посмотри на график. Как думаешь, через минуту цена будет выше или ниже, чем сейчас? Жми кнопку!' },
    wait: { pose: 'wait', text: 'Сделка открыта! Цена входа — первая сделка на бирже после твоей заявки. Полоска над кнопками показывает, как идут дела. Ждём итог…' },
    result: !result
      ? { pose: 'wait', text: 'Фиксируем цену закрытия…' }
      : result.status === 'win'
        ? { pose: 'profit', text: `Прибыль +${money((result.payoutCents ?? 0) - result.stakeCents)} Д — прогноз оказался верным! Давай посмотрим, откуда взялись цены.` }
        : result.status === 'loss'
          ? { pose: 'calm', text: `Бывает: −${money(result.stakeCents)} Д. В трейдинге важна не одна сделка, а доля верных прогнозов на дистанции. Посмотрим, как всё было.` }
          : { pose: 'think', text: 'Цена не изменилась — сумма вернулась. Посмотрим, как это выглядит в чеке.' },
    verify: { pose: 'teach', text: 'Это чек: вход и выход — настоящие сделки Binance с номерами. Нажми «Проверить на Binance» — телефон сам спросит биржу, мимо нашего сервера.' },
  };
  const { pose, text } = lines[step];
  const index = COACH_ORDER.indexOf(step);
  return (
    <div className={`coach-card ${step === 'pair' ? 'bottom' : 'top'}`} role="dialog" aria-label="Обучение" key={step}>
      <Mascot pose={pose} size={76} />
      <div className="coach-body">
        <small className="coach-step">Обучение · шаг {index + 1} из {COACH_ORDER.length}</small>
        <p>{text}</p>
        <div className="coach-actions">
          {step === 'verify' ? <span /> : <button className="link-btn" onClick={onSkip}>Пропустить</button>}
          {(step === 'pair' || step === 'time' || step === 'stake') && <button className="primary" onClick={onNext}>Дальше</button>}
          {step === 'result' && result && <button className="primary" onClick={onReceipt}>Открыть чек</button>}
          {step === 'verify' && <button className="primary" onClick={onNext}>Готово</button>}
        </div>
      </div>
    </div>
  );
}

// First run: three short screens on what this is. Shown once; reopened from the rules sheet.
function Onboarding({ onDone }: { onDone: () => void }) {
  const [i, setI] = useState(0);
  const slide = SLIDES[i],
    last = i === SLIDES.length - 1;
  return (
    <div className="onboarding" role="dialog" aria-modal="true" aria-label="Знакомство с Direction Lab">
      <button className="onb-skip link-btn" onClick={onDone}>Пропустить</button>
      <div className="onb-art" key={i}>
        <Mascot pose={slide.pose} size={240} />
      </div>
      <h2>{slide.title}</h2>
      <p>{slide.text}</p>
      <div className="onb-dots" aria-hidden>
        {SLIDES.map((_, k) => (
          <i key={k} className={k === i ? 'on' : ''} />
        ))}
      </div>
      <button className="primary" onClick={() => (last ? onDone() : setI(i + 1))}>{last ? 'Начать' : 'Дальше'}</button>
    </div>
  );
}

export default function Terminal() {
  const [raw, setS] = useState<Snapshot | null>(null);
  const [assetId, setAsset] = useState('BTCUSDT'),
    [period, setPeriod] = useState(15);
  const [stake, setStake] = useState(10),
    [mode, setMode] = useState<'timer' | 'time'>('timer'),
    [duration, setDuration] = useState(60),
    [atMs, setAtMs] = useState(0);
  const [busy, setBusy] = useState(false),
    [pending, setPending] = useState<Action | null>(null),
    [error, setError] = useState(''),
    [network, setNetwork] = useState(''),
    [elapsed, setElapsed] = useState(0);
  const [duelFocus, setDuelFocus] = useState(''),
    [onboarding, setOnboarding] = useState(false);
  const [coach, setCoach] = useState<{ step: CoachStep; known: string[]; tradeId?: string } | null>(null);
  const finishOnboarding = () => {
    setOnboarding(false);
    try {
      localStorage.setItem(ONBOARDED, '1');
    } catch {}
    track('onboarded');
    // a new trader goes straight on to the guided first trade
    let seen = false;
    try {
      seen = !!localStorage.getItem(TUTORIAL);
    } catch {}
    if (!seen && !s?.trades.length) startCoach();
  };
  const startCoach = () => {
    setSheet('');
    setAsset('BTCUSDT');
    setMode('timer');
    setDuration(60);
    setStake(10);
    setCoach({ step: 'pair', known: s?.trades.map((t) => t.id) ?? [] });
    track('tutorial_started');
  };
  const endCoach = (done: boolean) => {
    setCoach(null);
    try {
      localStorage.setItem(TUTORIAL, done ? 'done' : 'skipped');
    } catch {}
    track(done ? 'tutorial_done' : 'tutorial_skipped');
  };
  const [sheet, setSheet] = useState<'' | 'asset' | 'stake' | 'time' | 'trades' | 'info' | 'receipt' | 'top' | 'duels'>(''),
    [filter, setFilter] = useState('active'),
    [toasts, setToasts] = useState<Toast[]>([]),
    [stakeDraft, setStakeDraft] = useState(''),
    [receiptId, setReceiptId] = useState(''),
    [pulseOn, setPulseOn] = useState(false),
    [autoReceipt, setAutoReceipt] = useState(false),
    [chartKind, setChartKind] = useState<'candles' | 'line'>('candles'),
    [flash, setFlash] = useState(0),
    [flashAt, setFlashAt] = useState(0);
  const current = useRef<Snapshot | null>(null),
    selected = useRef({ assetId, period }),
    received = useRef(0),
    busyRef = useRef(false),
    pendingRef = useRef<Action | null>(null),
    alive = useRef(true),
    seen = useRef<Map<string, string> | null>(null),
    autoReceiptRef = useRef(autoReceipt),
    referralSeen = useRef<Referral | null>(null),
    duelSeen = useRef<Map<string, string> | null>(null),
    assetsCache = useRef<{ version: string; list: Asset[] }>({ version: '', list: [] }),
    sheetRef = useRef(sheet),
    pulse = useRef({ id: '', side: 0, at: 0, beat: '' });
  selected.current = { assetId, period };
  autoReceiptRef.current = autoReceipt;
  vibroOn = pulseOn;
  sheetRef.current = sheet;

  // Live trades of the pair on screen (Server-Sent Events from our server, which relays Binance's
  // trade stream). Between the once-a-second snapshots they move the price and the newest candles.
  const feed = useRef<{ key: string; last: Tick | null; bars: Map<number, { high: number; low: number; close: number }> }>({ key: '', last: null, bars: new Map() });
  const [feedN, setFeedN] = useState(0),
    [visible, setVisible] = useState(true);
  useEffect(() => {
    const on = () => setVisible(document.visibilityState !== 'hidden');
    on();
    document.addEventListener('visibilitychange', on);
    return () => document.removeEventListener('visibilitychange', on);
  }, []);
  useEffect(() => {
    if (!visible || typeof EventSource === 'undefined') return;
    const key = `${assetId}:${period}`;
    feed.current = { key, last: null, bars: new Map() };
    const es = new EventSource(`/api/stream?asset=${encodeURIComponent(assetId)}`);
    let frame = 0;
    es.onmessage = (e) => {
      let t: Tick;
      try {
        t = JSON.parse(e.data);
      } catch {
        return;
      }
      const f = feed.current;
      if (f.key !== key || (f.last && Number(t.id) <= Number(f.last.id))) return;
      f.last = t;
      const time = Math.floor(t.atMs / (period * 1000)) * period * 1000,
        b = f.bars.get(time);
      f.bars.set(time, b ? { high: Math.max(b.high, t.hi), low: Math.min(b.low, t.lo), close: t.price } : { high: t.hi, low: t.lo, close: t.price });
      if (f.bars.size > 120) f.bars.delete(f.bars.keys().next().value as number);
      // at most one redraw per screen frame, however busy the pair is
      if (!frame) frame = requestAnimationFrame(() => ((frame = 0), setFeedN((n) => n + 1)));
    };
    return () => {
      es.close();
      cancelAnimationFrame(frame);
    };
  }, [assetId, period, visible]);
  // The snapshot with the live trades applied: newer price, updated last candle, new candles.
  const s = useMemo(() => {
    const f = feed.current,
      t = f.last;
    if (!raw || !t || f.key !== `${raw.marketAssetId}:${raw.marketPeriodSeconds}`) return raw;
    if (raw.quote && Number(raw.quote.tradeId) >= Number(t.id)) return raw;
    const quote: Quote = { source: raw.quote?.source ?? 'Binance Spot', assetId: raw.marketAssetId, tradeId: t.id, price: t.price, atMs: t.atMs, receivedAtMs: t.atMs };
    const candles = raw.candles.map((c) => ({ ...c }));
    const lastTime = candles.at(-1)?.time ?? -Infinity;
    for (const [time, b] of [...f.bars.entries()].sort((x, y) => x[0] - y[0])) {
      const c = candles.find((x) => x.time === time);
      if (c) {
        c.high = Math.max(c.high, b.high);
        c.low = Math.min(c.low, b.low);
        if (time === lastTime) c.close = b.close;
      } else if (time > lastTime) {
        const open = candles.at(-1)?.close ?? b.close;
        candles.push({ time, open, high: Math.max(open, b.high), low: Math.min(open, b.low), close: b.close });
      }
    }
    return { ...raw, quote, candles: candles.slice(-60) };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [raw, feedN]);

  // Small cards stacked at the bottom; after 3 s each one floats away (the exit animation runs
  // before it is removed). At most 3 at a time.
  const pushToast = useCallback((t: Toast) => {
    setToasts((list) => [...list.filter((x) => x.id !== t.id), t].slice(-3));
    setTimeout(() => setToasts((list) => list.map((x) => (x.id === t.id ? { ...x, leaving: true } : x))), 3000);
    setTimeout(() => setToasts((list) => list.filter((x) => x.id !== t.id)), 3450);
  }, []);

  const accept = useCallback(
    (next: Snapshot) => {
      // The pair list only comes when it changed; otherwise reuse the cached one.
      if (next.assets) assetsCache.current = { version: next.assetsVersion, list: next.assets };
      else next.assets = assetsCache.current.list;
      // results of finished trades → toast + haptics, regardless of the chart selection
      const prev = seen.current;
      const map = new Map(next.trades.map((t) => [t.id, t.status]));
      let receipt = '';
      if (prev)
        for (const t of next.trades) {
          const was = prev.get(t.id);
          if (!was || was === t.status || (t.status === 'open' && was === 'pending')) continue;
          if (t.status === 'open' || t.status === 'pending') continue;
          const a = next.assets.find((x) => x.id === t.assetId);
          const net = (t.payoutCents ?? 0) - t.stakeCents;
          pushToast({
            id: t.id,
            tone: t.status === 'win' ? 'win' : t.status === 'loss' ? 'loss' : 'flat',
            title: t.status === 'win' ? `+${money(net)} Д` : t.status === 'loss' ? `−${money(t.stakeCents)} Д` : 'Сумма возвращена',
            body: `${a?.name ?? t.assetId} · ${t.direction === 'up' ? 'выше' : 'ниже'}${t.status === 'void' ? '' : ' · чек →'}`,
            asset: a,
            tradeId: t.status === 'void' ? undefined : t.id,
          });
          haptic(t.status === 'win' ? 'success' : t.status === 'loss' ? 'error' : 'warning');
          if (t.status !== 'void') receipt = t.id;
        }
      seen.current = map;
      // Referral bonuses arrive as plain balance changes: tell the player where they came from.
      const ref = next.referral,
        was = referralSeen.current;
      if (ref && was) {
        if (!was.bonusPaid && ref.bonusPaid) pushToast({ id: 'ref-invited', pose: 'profit', tone: 'win', title: `+${whole(ref.bonusCents)} Д`, body: 'Бонус за приглашение — добро пожаловать!' });
        if (ref.played > was.played) pushToast({ id: 'ref-inviter', pose: 'profit', tone: 'win', title: `+${whole((ref.played - was.played) * ref.bonusCents)} Д`, body: 'Приглашённый друг сделал первую сделку' });
      } else if (ref?.invitedBy && !ref.bonusPaid) {
        try {
          if (!localStorage.getItem('direction-lab-invited-v1')) {
            localStorage.setItem('direction-lab-invited-v1', '1');
            pushToast({ id: 'ref-hello', pose: 'hello', tone: 'info', title: `Вас пригласил(а) ${ref.invitedBy}`, body: `После первой сделки вы оба получите +${whole(ref.bonusCents)} Д` });
          }
        } catch {}
      }
      if (ref) referralSeen.current = ref;
      // Duel news (accepted, result, expired) as toasts, wherever the player is in the app.
      if (next.duels) {
        const before = duelSeen.current;
        if (before)
          for (const d of next.duels) {
            const was = before.get(d.id);
            if (!was || was === d.status) continue;
            const rival = d.you === 'creator' ? d.opponent?.name : d.creator.name;
            if (d.status === 'pending' && d.you === 'creator') {
              pushToast({ id: `duel-${d.id}`, pose: 'duel', tone: 'info', title: `${rival} принял(а) вызов`, body: 'Дуэль начинается — итог через минуту' });
              haptic('impact');
            } else if (d.status === 'won') {
              const mine = d.winner === d.you;
              pushToast({ id: `duel-${d.id}`, pose: mine ? 'champion' : 'calm', tone: mine ? 'win' : 'loss', title: mine ? `+${amt(d.stakeCents - (d.feeCents ?? 0))} Д` : `−${whole(d.stakeCents)} Д`, body: mine ? `Дуэль с ${rival} выиграна` : `Дуэль с ${rival} проиграна` });
              haptic(mine ? 'success' : 'error');
            } else if (d.status === 'tie' || d.status === 'void') {
              pushToast({ id: `duel-${d.id}`, pose: 'think', tone: 'flat', title: 'Суммы вернулись', body: d.status === 'tie' ? `Ничья в дуэли с ${rival}` : 'Биржа не подтвердила цену' });
              haptic('warning');
            } else if (d.status === 'expired') pushToast({ id: `duel-${d.id}`, pose: 'think', tone: 'info', title: 'Вызов никто не принял', body: `${whole(d.stakeCents)} Д вернулись на счёт` });
          }
        duelSeen.current = new Map(next.duels.map((d) => [d.id, d.status]));
      }
      // The receipt opens by itself only when nothing else is on screen.
      if (receipt && autoReceiptRef.current && !sheetRef.current) {
        setReceiptId(receipt);
        setSheet('receipt');
      }
      if (next.marketAssetId !== selected.current.assetId || next.marketPeriodSeconds !== selected.current.period) return;
      const old = current.current;
      if (old && next.serverNowMs < old.serverNowMs) return;
      current.current = next;
      received.current = performance.now();
      setElapsed(0);
      setS(next);
      setNetwork('');
      setAtMs((t) => (next.absoluteSlots.includes(t) ? t : next.absoluteSlots[0]));
    },
    [pushToast],
  );
  const query = () => `asset=${selected.current.assetId}&period=${selected.current.period}&assets=${assetsCache.current.version}`;
  const refresh = useCallback(async () => {
    try {
      const r = await fetch(`/api/state?${query()}`, { cache: 'no-store', headers: auth(), signal: AbortSignal.timeout(10_000) });
      const reply = await r.json();
      if (!alive.current) return;
      if (!r.ok) throw new Error(reply.error?.message || 'Не удалось загрузить счёт.');
      accept(reply as Snapshot);
    } catch (e) {
      if (alive.current) setNetwork(e instanceof Error ? e.message : 'Нет связи с сервером.');
    }
  }, [accept]);

  useEffect(() => {
    alive.current = true;
    const p = readPrefs();
    if (typeof p.assetId === 'string') setAsset(p.assetId);
    if (typeof p.stake === 'number') setStake(p.stake);
    if (typeof p.duration === 'number') setDuration(p.duration);
    if (p.mode === 'time' || p.mode === 'timer') setMode(p.mode);
    if (typeof p.vibro === 'boolean') setPulseOn(p.vibro);
    if (typeof p.receiptNow === 'boolean') setAutoReceipt(p.receiptNow);
    if (p.chartKind === 'line' || p.chartKind === 'candles') setChartKind(p.chartKind);
    try {
      const saved = sessionStorage.getItem(PENDING);
      if (saved) {
        const a = JSON.parse(saved);
        if (a && typeof a.path === 'string' && typeof a.body?.id === 'string') {
          pendingRef.current = a;
          setPending(a);
        }
      }
    } catch {}
    const w = tg();
    w?.ready();
    w?.expand();
    w?.disableVerticalSwipes?.();
    // A duel challenge link opens straight to that challenge (?duel=<id> or start_param duel_<id>).
    try {
      if (!localStorage.getItem(ONBOARDED)) setOnboarding(true);
    } catch {}
    const fromLink = new URLSearchParams(window.location.search).get('duel') ?? /^duel_([A-Za-z0-9_-]{16,100})$/.exec(w?.initDataUnsafe?.start_param ?? '')?.[1];
    if (fromLink && /^[A-Za-z0-9_-]{16,100}$/.test(fromLink)) {
      setDuelFocus(fromLink);
      setSheet('duels');
    }
    const id = setInterval(() => received.current && setElapsed(performance.now() - received.current), 250);
    return () => {
      alive.current = false;
      clearInterval(id);
    };
  }, []);
  useEffect(() => {
    try {
      localStorage.setItem(PREFS, JSON.stringify({ assetId, stake, duration, mode, vibro: pulseOn, receiptNow: autoReceipt, chartKind }));
    } catch {}
  }, [assetId, stake, duration, mode, pulseOn, autoReceipt, chartKind]);
  useEffect(() => {
    let stopped = false,
      running = false;
    const poll = async () => {
      if (stopped || running || document.visibilityState === 'hidden') return;
      running = true;
      try {
        await refresh();
      } finally {
        running = false;
      }
    };
    void poll();
    const id = setInterval(poll, 1000);
    document.addEventListener('visibilitychange', poll);
    return () => {
      stopped = true;
      clearInterval(id);
      document.removeEventListener('visibilitychange', poll);
    };
  }, [assetId, period, refresh]);

  const remember = (a: Action | null) => {
    pendingRef.current = a;
    setPending(a);
    try {
      if (a) sessionStorage.setItem(PENDING, JSON.stringify(a));
      else sessionStorage.removeItem(PENDING);
    } catch {}
  };
  const send = async (path: string | null, payload?: Record<string, unknown>) => {
    if (busyRef.current || !current.current) return;
    if (path && pendingRef.current) {
      setError('Сначала дождитесь ответа на предыдущую заявку.');
      return;
    }
    const a: Action | null = path ? { path, body: { id: crypto.randomUUID(), ...payload } } : pendingRef.current;
    if (!a) return;
    remember(a);
    busyRef.current = true;
    setBusy(true);
    setError('');
    try {
      const r = await fetch(a.path, { method: 'POST', headers: { 'Content-Type': 'application/json', ...auth() }, body: JSON.stringify(a.body), signal: AbortSignal.timeout(12_000) });
      const reply: Reply = await r.json();
      if (r.status >= 500) throw new Error(reply.error?.message || 'Сервер не ответил.');
      remember(null);
      if (!r.ok) {
        setError(reply.error?.message || 'Заявка отклонена.');
        haptic('error');
        return;
      }
      if (a.path === '/api/orders' && !reply.replayed) haptic('impact');
      if (a.path === '/api/demo/topup') pushToast({ id: 'topup', tone: 'info', title: 'Демо-счёт пополнен', body: `Баланс ${money(reply.balanceCents ?? 0)} Д.` });
    } catch (e) {
      setError(`${e instanceof Error ? e.message : 'Нет ответа.'} Нажмите «Повторить»: заявка не спишется дважды.`);
    } finally {
      busyRef.current = false;
      setBusy(false);
      void refresh();
    }
  };
  const exportJournal = async () => {
    try {
      const r = await fetch('/api/export', { cache: 'no-store', headers: auth() });
      if (!r.ok) throw new Error('Не удалось скачать журнал.');
      const url = URL.createObjectURL(await r.blob()),
        link = document.createElement('a');
      link.href = url;
      link.download = `direction-lab-${new Date().toISOString().slice(0, 10)}.json`;
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ошибка выгрузки.');
    }
  };

  const shownBalance = useTween(s?.balanceCents ?? 0);
  const marketReady = !!s && s.marketAssetId === assetId && s.marketPeriodSeconds === period;
  const asset = s?.assets.find((a) => a.id === assetId) ?? s?.assets[0];
  const now = s ? s.asOfMs + elapsed : 0;
  const stale = !marketReady || elapsed > 8000;
  // Fresh = the newest exchange trade (live feed or snapshot) is recent enough for this pair.
  // Binance answering but no trades for a while is "quiet": amber, chart stays, a counter runs.
  const quoteAge = s?.quote ? now - s.quote.atMs : Infinity;
  const feedOk = marketReady && (s?.market.status === 'live' || s?.market.status === 'quiet') && !!s?.quote;
  const live = feedOk && quoteAge <= (asset?.liveMaxAgeMs ?? 10_000);
  const quietFor = feedOk && !live ? Math.max(0, Math.round(quoteAge / 1000)) : null;
  const cents = Math.round(stake * 100);
  const maxStake = asset ? asset.maxStakeCents / 100 : 1000;
  const stakes = STAKES.filter((n) => n <= maxStake);
  const profit = asset ? Math.floor((cents * asset.payoutBps) / 10_000) : 0;
  const tooMuch = !!s && cents > s.balanceCents;
  const full = !!s && s.stats.open >= s.policy.maxActive;
  const durations = s?.policy.durations ?? [15, 30, 60, 120, 180, 300];
  // durations the server's fairness check has closed for this pair (too predictable right now)
  const closedDurations = marketReady ? (s?.market.blockedDurations ?? []) : [];
  const openDurations = durations.filter((d) => !closedDurations.includes(d));
  const durationClosed = mode === 'timer' && closedDurations.includes(duration);
  const disabled = busy || !!pending || stale || !live || !s || tooMuch || full || durationClosed || cents > (asset?.maxStakeCents ?? 0);
  const receiptTrade = s?.trades.find((t) => t.id === receiptId) ?? null;
  const openReceipt = (id: string) => {
    setReceiptId(id);
    setSheet('receipt');
  };
  const previewExpiry = mode === 'timer' ? now + duration * 1000 : atMs || now + 60_000;
  const activeCount = s?.stats.open ?? 0;

  useEffect(() => {
    if (asset && stake > maxStake) setStake(maxStake);
  }, [asset, stake, maxStake]);

  // "Pulse": the open trade on the chart closest to expiry, felt through haptics.
  const watched = useMemo(
    () =>
      live && s?.quote
        ? (s.trades.filter((t) => t.assetId === assetId && t.status === 'open' && t.openPrice !== null && t.expiresAtMs > now).sort((a, b) => a.expiresAtMs - b.expiresAtMs)[0] ?? null)
        : null,
    [live, s, assetId, now],
  );
  // When the watched trade's time is up, its strip stays: "closing price…" until the exchange trade
  // is fixed, then the result for a moment, then it fades out.
  const [ending, setEnding] = useState<{ id: string; settled: boolean } | null>(null);
  const lastWatched = useRef<string>('');
  useEffect(() => {
    const was = lastWatched.current;
    lastWatched.current = watched?.id ?? '';
    if (was && !watched) setEnding({ id: was, settled: false });
  }, [watched]);
  const endTrade = ending ? (s?.trades.find((t) => t.id === ending.id) ?? null) : null;
  const endSettled = !!endTrade && endTrade.status !== 'open' && endTrade.status !== 'pending';
  useEffect(() => {
    if (!ending) return;
    if (endSettled && !ending.settled) return setEnding({ ...ending, settled: true });
    // the result shows for 1.6 s; a close that takes unusually long just lets the strip go
    const id = setTimeout(() => setEnding(null), ending.settled ? 1600 : 20_000);
    return () => clearTimeout(id);
  }, [ending, endSettled]);
  const watchedSide = watched && s?.quote ? Math.sign(s.quote.price - (watched.openPrice as number)) * (watched.direction === 'up' ? 1 : -1) : 0;
  const watchedLeft = watched ? watched.expiresAtMs - now : 0;
  // A tap each time the price crosses the entry level (at most every 1.5 s, so a price hovering
  // around the entry doesn't buzz constantly).
  useEffect(() => {
    if (!watched) return;
    const p = pulse.current;
    if (p.id !== watched.id) {
      pulse.current = { id: watched.id, side: watchedSide, at: 0, beat: '' };
      return;
    }
    if (watchedSide !== 0 && p.side !== 0 && watchedSide !== p.side && performance.now() - p.at > 1500) {
      p.at = performance.now();
      setFlash((n) => n + 1);
      setFlashAt(p.at);
      if (pulseOn && document.visibilityState === 'visible') haptic(watchedSide > 0 ? 'rigid' : 'soft');
    }
    if (watchedSide !== 0) p.side = watchedSide;
  }, [watched, watchedSide, pulseOn]);
  // Heartbeat in the last 10 seconds: once a second, twice a second in the last 3.
  useEffect(() => {
    if (!watched || !pulseOn || watchedLeft > 10_000 || watchedLeft <= 0) return;
    const step = watchedLeft <= 3000 ? 500 : 1000;
    const beat = `${watched.id}:${step}:${Math.ceil(watchedLeft / step)}`;
    if (pulse.current.beat === beat) return;
    pulse.current.beat = beat;
    if (document.visibilityState === 'visible') haptic('light');
  }, [watched, watchedLeft, pulseOn]);

  const stepStake = (dir: 1 | -1) => {
    const i = stakes.findIndex((n) => n >= stake);
    const idx = i === -1 ? stakes.length - 1 : stakes[i] === stake ? i + dir : dir > 0 ? i : i - 1;
    setStake(stakes[Math.max(0, Math.min(stakes.length - 1, idx))]);
  };
  const stepTime = (dir: 1 | -1) => {
    if (mode === 'timer') {
      const i = openDurations.indexOf(duration);
      setDuration(i < 0 ? (openDurations[0] ?? 60) : (openDurations[Math.max(0, Math.min(openDurations.length - 1, i + dir))] ?? 60));
    } else if (s) {
      const i = Math.max(0, s.absoluteSlots.indexOf(atMs));
      setAtMs(s.absoluteSlots[Math.max(0, Math.min(s.absoluteSlots.length - 1, i + dir))]);
    }
  };
  // the chosen duration got closed for this pair: move to the nearest open one and say why
  useEffect(() => {
    if (!durationClosed || !openDurations.length || !asset) return;
    setDuration(openDurations.find((d) => d > duration) ?? openDurations[0]);
    pushToast({
      id: `closed-${asset.id}-${duration}`,
      pose: 'think',
      tone: 'info',
      title: `${asset.name}: срок ${durationLabel(duration)} закрыт`,
      body: 'На коротком сроке цена этой пары сейчас слишком предсказуема — так честнее для всех.',
    });
  }, [durationClosed, openDurations, duration, asset, pushToast]);
  // the tutorial follows the trader's own trade: placed → waiting → result
  const coachTrade = coach?.tradeId ? (s?.trades.find((t) => t.id === coach.tradeId) ?? null) : null;
  useEffect(() => {
    if (!coach || !s) return;
    if (coach.step === 'go') {
      const t = s.trades.find((x) => !coach.known.includes(x.id)); // the trade placed during the tutorial
      if (t) setCoach({ ...coach, step: 'wait', tradeId: t.id });
    } else if (coach.step === 'wait' && coachTrade && coachTrade.status !== 'open' && coachTrade.status !== 'pending') setCoach({ ...coach, step: 'result' });
  }, [coach, s, coachTrade]);
  useEffect(() => {
    document.body.dataset.coach = coach?.step ?? '';
  }, [coach?.step]);
  const coachNext = () => {
    if (!coach) return;
    if (coach.step === 'verify') return endCoach(true);
    setCoach({ ...coach, step: COACH_ORDER[COACH_ORDER.indexOf(coach.step) + 1] });
  };
  const order = (direction: 'up' | 'down') =>
    void send('/api/orders', { assetId, direction, stakeCents: cents, expiry: mode === 'timer' ? { mode, seconds: duration } : { mode, atMs } });
  const reason = !s
    ? ''
    : quietFor !== null
      ? `Тихий рынок: на Binance нет сделок по ${asset?.name ?? 'паре'} уже ${quietFor} с. Кнопки включатся с первой новой сделкой.`
      : !live
        ? s.market.message
      : full
        ? `Открыто ${s.policy.maxActive} сделок — это максимум. Дождитесь закрытия одной из них.`
        : tooMuch
          ? 'На балансе меньше выбранной суммы.'
          : stale
            ? 'Обновляем данные…'
            : '';
  // Asset picker: search, "hot" pairs (biggest 24 h range) on top, then groups. Only listed pairs.
  const [search, setSearch] = useState('');
  const { hot, groups } = useMemo(() => {
    const q = search.trim().toUpperCase().replace(/[\s/]/g, '');
    const open = (s?.assets ?? []).filter((a) => a.listed !== false && (!q || a.id.includes(q)));
    const hot = q ? [] : open.filter((a) => a.group !== 'Валюты' && (a.range24h ?? 0) > 0).sort((a, b) => (b.range24h ?? 0) - (a.range24h ?? 0)).slice(0, 8);
    const order = ['Крупные', 'Альткоины', 'Валюты'];
    const m = new Map<string, Asset[]>();
    for (const a of [...open].sort((a, b) => (b.volumeM ?? 0) - (a.volumeM ?? 0))) m.set(a.group, [...(m.get(a.group) ?? []), a]);
    return { hot, groups: [...m.entries()].sort((a, b) => (order.indexOf(a[0]) + 99) % 99 - ((order.indexOf(b[0]) + 99) % 99)) };
  }, [s?.assets, search]);
  // A pair that went quiet leaves the list; switch away from it unless a trade on it is still open.
  useEffect(() => {
    if (!s?.assets.length) return;
    const current = s.assets.find((a) => a.id === assetId);
    if ((!current || current.listed === false) && !s.trades.some((t) => t.assetId === assetId && (t.status === 'open' || t.status === 'pending'))) {
      setAsset('BTCUSDT');
      if (current && assetId !== 'BTCUSDT')
        pushToast({ id: `paused-${assetId}`, pose: 'think', tone: 'info', title: `${current.name} на паузе`, body: 'Сейчас по этой паре мало сделок на Binance. Переключили на BTC — пара вернётся, когда торги оживут.' });
    }
  }, [s, assetId, pushToast]);
  const pick = (id: string) => {
    setAsset(id);
    setSearch('');
    setSheet('');
  };
  const assetRow = (a: Asset) => (
    <button key={a.id} className={`asset-row ${a.id === assetId ? 'on' : ''}`} onClick={() => pick(a.id)}>
      <AssetIcon a={a} />
      <span className="asset-text">
        <strong>{a.name}</strong>
        <small>
          {a.change24h !== undefined && <b className={a.change24h >= 0.05 ? 'win' : a.change24h <= -0.05 ? 'loss' : ''}>{pct(a.change24h, true)}</b>}
          {a.range24h !== undefined ? ` · размах ${pct(a.range24h)} за сутки` : ` · сделка до ${money(a.maxStakeCents)} Д`}
        </small>
      </span>
      <span className="pct">{a.payoutBps / 100}%</span>
    </button>
  );

  return (
    <div className="app">
      <header className="top">
        <button className="asset-btn" onClick={() => setSheet('asset')} aria-haspopup="dialog" disabled={!s}>
          <AssetIcon a={asset} />
          <span className="asset-text">
            <strong>{asset ? asset.name.replace(' / USDT', '') : 'Загрузка'}</strong>
            <small>{asset ? `доход ${asset.payoutBps / 100}%` : ''}</small>
          </span>
          <span className="chev" aria-hidden>▾</span>
        </button>
        <button className="top-btn duel-btn" onClick={() => setSheet('duels')} aria-haspopup="dialog" aria-label="Дуэли один на один" disabled={!s}>
          <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden>
            <path d="M4 4l9 9M4 4h4M4 4v4M20 4l-9 9M20 4h-4M20 4v4M7 14l3 3M17 14l-3 3M5 19l3-3M19 19l-3-3" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          {!!s?.duels?.some((d) => d.status === 'live' || d.status === 'pending') && <i className="dot" />}
        </button>
        <button className="top-btn" onClick={() => setSheet('top')} aria-haspopup="dialog" aria-label="Рейтинг недели и приглашения" disabled={!s}>
          <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden>
            <path d="M8 4h8v5a4 4 0 0 1-8 0V4zM8 6H5a3 3 0 0 0 3 4M16 6h3a3 3 0 0 1-3 4M12 13v4M8.5 20h7M10 17h4v3h-4z" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" strokeLinecap="round" />
          </svg>
        </button>
        <button className="balance" onClick={() => setSheet('info')} aria-haspopup="dialog" disabled={!s}>
          <small>Демо-счёт</small>
          <strong>{s ? `${money(shownBalance)} Д` : '—'}</strong>
        </button>
      </header>

      <section className="chart" aria-label="График">
        <div className="chart-bar">
          <div className="chart-controls">
            <div className="periods" role="group" aria-label="Вид графика">
              <button className={chartKind === 'candles' ? 'on' : ''} aria-pressed={chartKind === 'candles'} aria-label="Свечи" onClick={() => setChartKind('candles')}>
                <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden>
                  <path d="M4 2v12M12 3v11M8 1v14" stroke="currentColor" strokeWidth="1.2" />
                  <rect x="2.5" y="5" width="3" height="5" rx="0.5" fill="currentColor" />
                  <rect x="6.5" y="3" width="3" height="7" rx="0.5" fill="currentColor" />
                  <rect x="10.5" y="7" width="3" height="4" rx="0.5" fill="currentColor" />
                </svg>
              </button>
              <button className={chartKind === 'line' ? 'on' : ''} aria-pressed={chartKind === 'line'} aria-label="Линия" onClick={() => setChartKind('line')}>
                <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden>
                  <path d="M1 12l4-5 3 3 3-6 4 4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round" />
                </svg>
              </button>
            </div>
            <div className="periods" role="group" aria-label={chartKind === 'line' ? 'Шаг линии' : 'Свечи'}>
              {[5, 15, 60].map((p) => (
                <button key={p} className={period === p ? 'on' : ''} aria-pressed={period === p} onClick={() => setPeriod(p)}>
                  {p === 60 ? '1 м' : `${p} с`}
                </button>
              ))}
            </div>
          </div>
          <span className={`feed ${live ? 'ok' : quietFor !== null ? 'quiet' : ''}`}>
            <i />
            {live ? `Binance · ${s?.quote ? clockMs(s.quote.atMs) : ''}` : quietFor !== null ? `Тихий рынок · ${quietFor} с` : marketReady ? 'Нет связи с Binance' : 'Подключаемся'}
          </span>
        </div>
        {s && asset && marketReady && s.quote && s.candles.length > 1 ? (
          <Chart s={s} asset={asset} now={now} previewExpiry={previewExpiry} kind={chartKind} flashAt={flashAt} />
        ) : (
          <div className="chart-empty">{!s ? 'Загружаем счёт и котировки…' : marketReady ? s.market.chartMessage || s.market.message : 'Загружаем график…'}</div>
        )}
      </section>

      <section className="ticket" aria-label="Новая сделка">
        {network && (
          <div className="notice warn" role="status">
            {network} <button className="link-btn" onClick={() => void refresh()}>Обновить</button>
          </div>
        )}
        {pending && (
          <div className="notice warn" role="status">
            Ответ на заявку не получен. <button className="link-btn" disabled={busy} onClick={() => void send(null)}>Повторить</button>
          </div>
        )}
        {error && <div className="notice bad" role="alert">{error}</div>}
        {!watched && endTrade && (
          <div
            className={`live-trade ${endSettled ? `leaving ${endTrade.status === 'win' ? 'win' : endTrade.status === 'loss' ? 'loss' : 'flat'}` : 'flat'}`}
            role="status"
          >
            <span className={`dir ${endTrade.direction}`} aria-hidden>{endTrade.direction === 'up' ? '↑' : '↓'}</span>
            <div className="lt-what">
              <strong>
                {endTrade.direction === 'up' ? 'Выше' : 'Ниже'} · {money(endTrade.stakeCents)} Д
              </strong>
              <small>{endSettled ? 'итог сделки' : 'фиксируем цену закрытия…'}</small>
            </div>
            <div className="lt-result">
              <strong>
                {!endSettled
                  ? '…'
                  : endTrade.status === 'win'
                    ? `+${money(endTrade.profitCents)} Д`
                    : endTrade.status === 'loss'
                      ? `−${money(endTrade.stakeCents)} Д`
                      : endTrade.status === 'tie'
                        ? 'В ноль'
                        : 'Возврат'}
              </strong>
              <small>{endSettled ? 'закрыта' : '0:00'}</small>
            </div>
          </div>
        )}
        {watched && s?.quote && asset && (
          <div
            key={`${watched.id}:${flash}`}
            className={`live-trade ${watchedSide > 0 ? 'win' : watchedSide < 0 ? 'loss' : 'flat'} ${watchedLeft <= 10_000 ? 'final' : ''} ${flash ? 'flash' : ''}`}
            role="status"
          >
            <span className={`dir ${watched.direction}`} aria-hidden>{watched.direction === 'up' ? '↑' : '↓'}</span>
            <div className="lt-what">
              <strong>
                {watched.direction === 'up' ? 'Выше' : 'Ниже'} · {money(watched.stakeCents)} Д
              </strong>
              <small>
                {fmt(watched.openPrice as number, asset)} → {fmt(s.quote.price, asset)}
              </small>
            </div>
            <div className="lt-result">
              <strong>{watchedSide > 0 ? `+${money(watched.profitCents)} Д` : watchedSide < 0 ? `−${money(watched.stakeCents)} Д` : 'В ноль'}</strong>
              <small>{countdown(watchedLeft)}</small>
            </div>
          </div>
        )}
        <div className="steppers">
          <div className="stepper">
            <button className="step" onClick={() => stepTime(-1)} aria-label="Меньше времени">−</button>
            <button className="step-value" onClick={() => setSheet('time')} aria-haspopup="dialog">
              <small>{mode === 'timer' ? 'Время' : 'Закрытие в'}</small>
              <strong>{mode === 'timer' ? durationLabel(duration) : atMs ? clock(atMs, false) : '—'}</strong>
            </button>
            <button className="step" onClick={() => stepTime(1)} aria-label="Больше времени">+</button>
          </div>
          <div className="stepper">
            <button className="step" onClick={() => stepStake(-1)} aria-label="Меньше сумма">−</button>
            <button
              className="step-value"
              onClick={() => {
                setStakeDraft(String(stake));
                setSheet('stake');
              }}
              aria-haspopup="dialog"
            >
              <small>Сумма</small>
              <strong>{money(cents)} Д</strong>
            </button>
            <button className="step" onClick={() => stepStake(1)} aria-label="Больше сумма">+</button>
          </div>
        </div>
        <div className="payout">
          <span>Прибыль при верном прогнозе</span>
          <strong>
            +{money(profit)} Д
          </strong>
        </div>
        <div className="go">
          <button className="go-btn up" disabled={disabled} onClick={() => order('up')}>
            <span aria-hidden>↑</span> Выше
          </button>
          <button className="go-btn down" disabled={disabled} onClick={() => order('down')}>
            <span aria-hidden>↓</span> Ниже
          </button>
        </div>
        <p className="hint">{reason || 'Цена входа — первая сделка на бирже после вашей заявки. Цена не изменилась — сумма возвращается.'}</p>
        <button className="trades-handle" onClick={() => setSheet('trades')} disabled={!s}>
          <span>Сделки</span>
          {activeCount > 0 && <b>{activeCount}</b>}
          {s && s.stats.netCents !== 0 && <em className={s.stats.netCents > 0 ? 'win' : 'loss'}>{signed(s.stats.netCents)} Д</em>}
          <span className="chev" aria-hidden>⌃</span>
        </button>
      </section>

      {s && (
        <aside className="side" aria-label="Сделки">
          <TradeList s={s} now={now} filter={filter} setFilter={setFilter} onExport={() => void exportJournal()} onReceipt={openReceipt} />
        </aside>
      )}

      {onboarding && <Onboarding onDone={finishOnboarding} />}
      {coach && !onboarding && (
        <Coach
          step={coach.step}
          trade={coachTrade}
          onNext={coachNext}
          onSkip={() => endCoach(false)}
          onReceipt={() => {
            if (coach.tradeId) openReceipt(coach.tradeId);
            setCoach({ ...coach, step: 'verify' });
          }}
        />
      )}

      <div className="toasts" aria-live="polite">
        {toasts.map((t) => (
          <button
            key={t.id}
            className={`toast ${t.tone} ${t.leaving ? 'leaving' : ''}`}
            onClick={() => {
              setToasts((list) => list.filter((x) => x.id !== t.id));
              if (t.tradeId) openReceipt(t.tradeId);
            }}
          >
            {/* trade results show the pair (with several open it is the quickest cue); one-off news shows the bull */}
            {t.asset ? <AssetIcon a={t.asset} /> : t.pose && <Mascot pose={t.pose} size={60} className="toast-mascot" />}
            {/^[+−]/.test(t.title) ? (
              <>
                <span>{t.body}</span>
                <strong>{t.title}</strong>
              </>
            ) : (
              <span className="toast-news">
                <b>{t.title}</b>
                <small>{t.body}</small>
              </span>
            )}
          </button>
        ))}
      </div>

      {s && (
        <>
          <Sheet title="Актив" open={sheet === 'asset'} onClose={() => setSheet('')}>
            <input className="asset-search" type="search" placeholder="Поиск: BTC, PEPE, SOL…" value={search} onChange={(e) => setSearch(e.target.value)} aria-label="Поиск пары" />
            {hot.length > 0 && (
              <div className="asset-group hot">
                <h3>Горячие — сильнее всего двигаются за сутки</h3>
                {hot.map(assetRow)}
              </div>
            )}
            {groups.map(([g, list]) => (
              <div key={g} className="asset-group">
                <h3>
                  {g} <span>{list.length}</span>
                </h3>
                {list.map(assetRow)}
              </div>
            ))}
            {!groups.length && <p className="empty">Такой пары нет в списке. Здесь только пары Binance с частыми сделками.</p>}
            <p className="fine">
              Список обновляется каждые 10 минут: только пары Binance к USDT с частыми сделками, иначе цену входа не зафиксировать. «Крупные» — 10 самых торгуемых, выплата выше.
              EUR/USDT — курс евро к стейблкоину на Binance: близок к EUR/USD, но меняется редко, ничьи там чаще.
            </p>
          </Sheet>

          <Sheet title="Сумма сделки" open={sheet === 'stake'} onClose={() => setSheet('')}>
            <label className="field">
              <span>Своя сумма, от 1 до {money(asset!.maxStakeCents)} Д</span>
              <input
                inputMode="decimal"
                value={stakeDraft}
                onChange={(e) => setStakeDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') (e.currentTarget.nextElementSibling as HTMLButtonElement | null)?.click();
                }}
              />
              <button
                className="primary"
                onClick={() => {
                  const n = Number(stakeDraft.replace(',', '.'));
                  if (/^\d{1,4}([.,]\d{1,2})?$/.test(stakeDraft) && n >= 1 && n <= maxStake) {
                    setStake(n);
                    setSheet('');
                  } else setStakeDraft(String(stake));
                }}
              >
                Готово
              </button>
            </label>
            <div className="chips">
              {stakes.map((n) => (
                <button
                  key={n}
                  className={stake === n ? 'on' : ''}
                  onClick={() => {
                    setStake(n);
                    setSheet('');
                  }}
                >
                  {n} Д
                </button>
              ))}
            </div>
          </Sheet>

          <Sheet title="Время сделки" open={sheet === 'time'} onClose={() => setSheet('')}>
            <div className="segmented">
              <button className={mode === 'timer' ? 'on' : ''} onClick={() => setMode('timer')}>Таймер</button>
              <button className={mode === 'time' ? 'on' : ''} onClick={() => setMode('time')}>Точное время</button>
            </div>
            <div className="chips">
              {mode === 'timer'
                ? durations.map((d) => (
                    <button
                      key={d}
                      className={`${duration === d ? 'on' : ''} ${closedDurations.includes(d) ? 'closed' : ''}`}
                      disabled={closedDurations.includes(d)}
                      onClick={() => {
                        setDuration(d);
                        setSheet('');
                      }}
                    >
                      {durationLabel(d)}
                      {closedDurations.includes(d) && <small>закрыт</small>}
                    </button>
                  ))
                : s.absoluteSlots.map((t) => (
                    <button
                      key={t}
                      className={atMs === t ? 'on' : ''}
                      onClick={() => {
                        setAtMs(t);
                        setSheet('');
                      }}
                    >
                      {clock(t, false)}
                    </button>
                  ))}
            </div>
            {mode === 'timer' && closedDurations.length > 0 && asset && (
              <p className="notice">
                Короткие сроки по {asset.name} сейчас закрыты: на них простая стратегия «по последним секундам» обыгрывает порог безубыточности. Сервер
                проверяет каждую пару раз в час и откроет сроки, когда перевес исчезнет.
              </p>
            )}
            <p className="fine">
              {mode === 'timer'
                ? 'Отсчёт идёт от цены входа: «1 мин» — ровно 60 секунд с момента первой биржевой сделки после заявки.'
                : `Сделка закроется в выбранную минуту по Москве. Ближайшее время — не раньше чем через ${(s.market.cutoffMs ?? s.policy.cutoffMs) / 1000} секунд.`}
            </p>
          </Sheet>

          <Sheet title="Сделки" open={sheet === 'trades'} onClose={() => setSheet('')}>
            <TradeList s={s} now={now} filter={filter} setFilter={setFilter} onExport={() => void exportJournal()} onReceipt={openReceipt} />
          </Sheet>

          <Sheet
            title="Дуэли 1 на 1"
            open={sheet === 'duels'}
            onClose={() => {
              setSheet('');
              setDuelFocus('');
            }}
          >
            <Duels s={s} focusId={duelFocus} />
          </Sheet>

          <Sheet title="Рейтинг недели" open={sheet === 'top'} onClose={() => setSheet('')}>
            <Leaderboard />
          </Sheet>

          <Sheet title="Чек сделки" open={sheet === 'receipt' && !!receiptTrade} onClose={() => setSheet('')}>
            {receiptTrade && <Receipt s={s} t={receiptTrade} onNew={() => setSheet('')} />}
          </Sheet>

          <Sheet title="Демо-счёт и правила" open={sheet === 'info'} onClose={() => setSheet('')}>
            <dl className="facts">
              <div><dt>Свободно</dt><dd>{money(s.balanceCents)} Д</dd></div>
              <div><dt>В сделках</dt><dd>{money(s.lockedCents)} Д</dd></div>
              <div><dt>Итог по сделкам</dt><dd className={s.stats.netCents > 0 ? 'win' : s.stats.netCents < 0 ? 'loss' : ''}>{signed(s.stats.netCents)} Д</dd></div>
            </dl>
            <h3>Как считается сделка</h3>
            <ul className="rules">
              <li>Цены — реальные сделки на бирже Binance. Демо-единицы Д бесплатные и ничего не стоят.</li>
              <li>Цена входа — первая сделка на бирже после того, как сервер принял заявку. Если сделок не было за {(s.market.entryWindowMs ?? s.policy.entryWindowMs) / 1000} секунд ({s.market.entryWindowMs && s.market.entryWindowMs > s.policy.entryWindowMs ? 'у этой пары торги идут рывками, поэтому окно длиннее' : 'у крупных пар'}), сумма возвращается. Таймер отсчитывается от входа, так что ожидание ничего не съедает.</li>
              <li>Цена закрытия — последняя сделка на бирже в момент закрытия или раньше.</li>
              <li>Цена выше входа — верен прогноз «Выше», ниже — «Ниже». Цена не изменилась — сумма возвращается.</li>
              <li>Если биржа временно не отвечает, сделка ждёт цену и не отменяется. Номера сделок Binance в журнале позволяют проверить каждую цену.</li>
            </ul>
            <button
              className="link-btn"
              onClick={() => {
                setSheet('');
                setOnboarding(true);
              }}
            >
              Как это работает — коротко, в 3 экранах →
            </button>
            <button className="link-btn" onClick={startCoach}>
              Пройти обучение с бычком: первая сделка по шагам →
            </button>
            <h3>Кошелёк TON</h3>
            <WalletLink s={s} onChange={() => void refresh()} />
            <h3>Настройки</h3>
            <label className="toggle">
              <input type="checkbox" checked={pulseOn} onChange={(e) => setPulseOn(e.target.checked)} />
              <span>
                Вибрация
                <small>Открытие и итог сделки, пересечение цены входа, последние 10 секунд</small>
              </span>
            </label>
            <label className="toggle">
              <input type="checkbox" checked={autoReceipt} onChange={(e) => setAutoReceipt(e.target.checked)} />
              <span>
                Открывать чек сразу после сделки
                <small>Иначе итог появляется карточкой внизу — нажмите на неё, чтобы открыть чек</small>
              </span>
            </label>
            <div className="info-actions">
              <button className="secondary" onClick={() => void exportJournal()}>Скачать журнал</button>
              <button
                className="secondary"
                disabled={busy || !!pending || s.stats.open > 0 || s.balanceCents >= s.policy.demoBalanceCents}
                onClick={() => void send('/api/demo/topup')}
              >
                Пополнить до {money(s.policy.demoBalanceCents)} Д
              </button>
            </div>
            <p className="fine">
              {s.stats.open > 0 ? 'Пополнить можно, когда нет открытых сделок. ' : ''}История сделок сохраняется. Аккаунт: {s.user.firstName ?? 'Telegram'}
              {s.user.username ? ` (@${s.user.username})` : ''}, ядро {s.coreVersion}.
            </p>
          </Sheet>
        </>
      )}
    </div>
  );
}
